import {
  ArrowTopRightOnSquareIcon,
  ChatBubbleLeftRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  InformationCircleIcon,
  KeyIcon,
  SparklesIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  ArrowDownTrayIcon,
  ClipboardDocumentCheckIcon,
  ClockIcon,
  DocumentDuplicateIcon,
  EyeIcon,
  EyeSlashIcon,
} from "@heroicons/react/24/solid";
import { useQuery } from "@tanstack/react-query";
import axios, { AxiosError, AxiosResponse } from "axios";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import ReactGA from "react-ga4";
import Plot from "react-plotly.js";
import { Link, Navigate, useNavigate } from "react-router-dom";

import AffiliateEarnings from "./AffiliateEarnings";
import UsageDetailCard from "./HubUsageDetail";
import RouteInsights from "./RouteInsights";

import { useApiKey } from "context/ApiKeyContext";
import UserContext from "context/UserContext";

import Badge from "components/atoms/Badge";
import Button from "components/atoms/Button";
import Card from "components/atoms/Card";
import EstimatedMonthlyCost from "components/atoms/EstimatedMonthlyCost";
import Spinner from "components/atoms/Spinner";
import { convertToNumLetter } from "components/molecules/PriceTables/PriceTableNutritionAPI";
import SubscriptionCard from "components/molecules/SubscriptionCard";

import { error, success } from "utils";
import { NUTRITION_PLATFORM_SLUG } from "utils/products";
import { ProductDataType, UsageHistoryReportForPeriod } from "utils/types";

// NOTE(PRICING_REFERENCE_TABLE):
// This table is an intentional UI reference for plan cards/estimator fallback.
// For subscribed-account billing summaries and Insights forecasts, use Stripe
// subscription payload values (see getSubscriptionMonthlyFeeUsd) instead.
const TIERS = [
  {
    name: "Starter",
    slug: "nutrition-platform-starter",
    price: "$99",
    priceNum: 99,
    tokens: "1M",
    tokensMillions: 1,
    refillPrice: "$25 / 1M",
    refillPricePerM: 25,
    features: {
      easyAI: true,
      localization: true,
      modelSwitching: false,
      insights: false,
      aiInsights: false,
      affiliateEarnings: true,
      support: "Email + Community Slack",
    },
    highlight: false,
  },
  {
    name: "Growth",
    slug: "nutrition-platform-growth",
    price: "$599",
    priceNum: 599,
    tokens: "5M",
    tokensMillions: 5,
    refillPrice: "$10 / 1M",
    refillPricePerM: 10,
    features: {
      easyAI: true,
      localization: true,
      modelSwitching: true,
      insights: true,
      aiInsights: false,
      affiliateEarnings: true,
      support: "Dedicated Slack Channel",
    },
    highlight: true,
  },
  {
    name: "Pro",
    slug: "nutrition-platform-pro",
    price: "$2,999",
    priceNum: 2999,
    tokens: "100M",
    tokensMillions: 100,
    refillPrice: "$2.50 / 1M",
    refillPricePerM: 2.5,
    features: {
      easyAI: true,
      localization: true,
      modelSwitching: true,
      insights: true,
      aiInsights: true,
      affiliateEarnings: true,
      support: "Enterprise Support",
    },
    highlight: false,
  },
];

const GLOBAL_ROUTE_AVG_DAYS = 30;
const DEFAULT_ROUTE_CALCULATOR_CATEGORIES = [
  "Image Detection",
  "Text & Audio",
  "Search",
  "API Calls",
];
const DEFAULT_ROUTE_CALLS_PER_USER_PER_DAY: Record<string, number> = {
  "Image Detection": 1,
  "Text & Audio": 2,
  Search: 5,
  "API Calls": 20,
};
const INFO_ICON_CLASS = "h-4 w-4 text-indigo-700";
const TIER_PILL_CLASS =
  "inline-flex items-center rounded-full bg-indigo-100 px-3 py-1 text-sm font-semibold text-indigo-700";
const SUPPORT_DOC_LINKS: Array<{ label: string; href: string }> = [
  { label: "Documentation", href: "https://passio.gitbook.io/nutrition-ai" },
  {
    label: "OpenAPI Docs",
    href: "https://api.passiolife.com/v2/products/docs",
  },
  {
    label: "Authorize With Your API Key",
    href: "https://passio.gitbook.io/nutrition-ai/rest-api/api-overview/authorization-setup",
  },
  { label: "Email Support", href: "mailto:support@passiolife.com" },
  {
    label: "Roadmap",
    href: "https://portal.productboard.com/passio/1-nutrition-ai/tabs/1-recently-launched",
  },
  {
    label: "Slack Invite Link",
    href: "https://join.slack.com/t/passio-community/shared_invite/zt-1ge2wpifb-FHhXT61e80gOPy~tzjX3Ng",
  },
  {
    label: "Slack Help Channel",
    href: "https://passio-community.slack.com/channels/help",
  },
];

type RouteAverageRow = {
  category: string;
  totalCalls: number;
  avgBilledTokens: number;
  totalBilledTokens: number;
};

type RawRouteAverageRow = {
  category?: string;
  totalCalls?: number | string;
  avgBilledTokens?: number | string;
  totalBilledTokens?: number | string;
  total_calls?: number | string;
  avg_billed_tokens?: number | string;
  total_billed_tokens?: number | string;
};

type EasyAISSOResponse = {
  ssoEnabled?: boolean;
  ssoUrl?: string;
  loginUrl?: string;
  expiresAt?: number;
};

type DetailsTab = "account" | "easyai" | "insights" | "affiliate";

const detailsTabToHash = (tab: DetailsTab): string => {
  switch (tab) {
    case "easyai":
      return "easy-ai";
    case "insights":
      return "insights";
    case "affiliate":
      return "affiliate-earnings";
    default:
      return "account";
  }
};

const getRequestedDetailsTab = (): DetailsTab => {
  const params = new URLSearchParams(window.location.search);
  const fromQuery = params.get("tab");
  const fromHash = window.location.hash.replace(/^#/, "");
  const normalized = String(fromQuery || fromHash || "account")
    .trim()
    .toLowerCase()
    .replace(/[^a-z]/g, "");

  if (normalized === "easyai" || normalized === "easy") return "easyai";
  if (normalized === "insights" || normalized === "insight") return "insights";
  if (
    normalized === "affiliate" ||
    normalized === "affiliateearnings" ||
    normalized === "earnings"
  ) {
    return "affiliate";
  }
  return "account";
};

const toNumber = (v: unknown): number => {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string") {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return 0;
};

const getUsageQuota = (usageInfo: any): number =>
  Math.max(0, Number(usageInfo?.unitsQuota ?? usageInfo?.unitQuota ?? 0) || 0);

const normalizeRouteCategory = (category: string): string => {
  const normalized = category.trim().toLowerCase();
  if (
    normalized === "semantic search" ||
    normalized === "search" ||
    normalized === "ai search"
  ) {
    return "Search";
  }
  if (
    normalized === "vision" ||
    normalized === "ai vision tools" ||
    normalized === "image detection"
  ) {
    return "Image Detection";
  }
  return category;
};

const normalizeRouteAverageRows = (raw: unknown): RouteAverageRow[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((row) => row as RawRouteAverageRow)
    .map((row) => ({
      category: normalizeRouteCategory(row.category ?? ""),
      totalCalls: toNumber(row.totalCalls ?? row.total_calls),
      avgBilledTokens: toNumber(row.avgBilledTokens ?? row.avg_billed_tokens),
      totalBilledTokens: toNumber(
        row.totalBilledTokens ?? row.total_billed_tokens
      ),
    }))
    .filter((row) => row.category !== "" && row.totalCalls > 0);
};

const formatTokenCount = (tokens: number): string => {
  if (!Number.isFinite(tokens) || tokens <= 0) return "0";
  if (tokens >= 1000000) {
    return `${(tokens / 1000000).toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })}M`;
  }
  if (tokens >= 1000) {
    return `${(tokens / 1000).toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })}K`;
  }
  return Math.round(tokens).toLocaleString();
};

const formatUsd = (amount: number): string =>
  amount.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });

const getSubscriptionMonthlyFeeUsd = (stripeSubscription: any): number => {
  if (!stripeSubscription) return 0;

  const items = stripeSubscription?.items?.data;
  if (Array.isArray(items) && items.length > 0) {
    let monthlyTotal = 0;

    items.forEach((item) => {
      const price = item?.price || {};
      const recurring = price?.recurring || {};
      const interval = String(recurring?.interval || "").toLowerCase();
      const intervalCount = Math.max(1, Number(recurring?.interval_count || 1));
      const quantity = Math.max(1, Number(item?.quantity || 1));

      const unitAmountCents = Number(price?.unit_amount || 0);
      const unitAmountDecimalCents = Number(price?.unit_amount_decimal || 0);
      const amountUsd =
        unitAmountCents > 0
          ? unitAmountCents / 100
          : unitAmountDecimalCents > 0
          ? unitAmountDecimalCents / 100
          : 0;
      if (amountUsd <= 0) return;

      const lineAmount = amountUsd * quantity;
      if (interval === "month") {
        monthlyTotal += lineAmount / intervalCount;
      } else if (interval === "year") {
        monthlyTotal += lineAmount / (12 * intervalCount);
      }
    });

    if (monthlyTotal > 0) return monthlyTotal;
  }

  const planAmount = Number(stripeSubscription?.plan?.amount || 0);
  if (planAmount > 0) return planAmount / 100;

  return 0;
};

const formatTierDisplayName = (tierRaw?: string): string => {
  const normalized = String(tierRaw || "")
    .trim()
    .toLowerCase()
    .replace("nutrition-platform-", "");
  if (!normalized) return "Unknown";
  return normalized
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const FALLBACK_PERIOD_SECONDS = 30 * 24 * 60 * 60;

const getRecurringPeriodSeconds = (stripeSubscription: any): number => {
  const recurring =
    stripeSubscription?.items?.data?.[0]?.price?.recurring ||
    stripeSubscription?.plan?.recurring ||
    {};
  const interval = String(recurring?.interval || "").toLowerCase();
  const intervalCount = Math.max(1, Number(recurring?.interval_count || 1));
  switch (interval) {
    case "day":
      return 24 * 60 * 60 * intervalCount;
    case "week":
      return 7 * 24 * 60 * 60 * intervalCount;
    case "year":
      return 365 * 24 * 60 * 60 * intervalCount;
    case "month":
      return 30 * 24 * 60 * 60 * intervalCount;
    default:
      return FALLBACK_PERIOD_SECONDS;
  }
};

const toInputDate = (unixSeconds: number): string =>
  new Date(unixSeconds * 1000).toISOString().split("T")[0];

const buildBillingPeriods = (
  stripeSubscription: any,
  maxPeriods = 24
): { start: number; end: number }[] => {
  if (!stripeSubscription) return [];

  let periodStart = Number(stripeSubscription.current_period_start || 0);
  let periodEnd = Number(stripeSubscription.current_period_end || 0);
  const subscriptionStart = Number(stripeSubscription.start_date || 0);

  if (!periodStart && !periodEnd) return [];

  if (!periodEnd && periodStart) {
    periodEnd = periodStart + getRecurringPeriodSeconds(stripeSubscription);
  }
  if (!periodStart && periodEnd) {
    periodStart = periodEnd - getRecurringPeriodSeconds(stripeSubscription);
  }

  if (periodStart > periodEnd) {
    const temp = periodStart;
    periodStart = periodEnd;
    periodEnd = temp;
  }

  let periodLength = periodEnd - periodStart;
  if (periodLength < 24 * 60 * 60) {
    periodLength = getRecurringPeriodSeconds(stripeSubscription);
    periodStart = periodEnd - periodLength;
  }

  if (periodLength < 24 * 60 * 60) {
    periodLength = FALLBACK_PERIOD_SECONDS;
    periodStart = periodEnd - periodLength;
  }

  const floorStart =
    subscriptionStart > 0
      ? subscriptionStart
      : periodStart - periodLength * maxPeriods;

  const periods: { start: number; end: number }[] = [];
  for (let i = 0; i < maxPeriods; i++) {
    const end = periodEnd - i * periodLength;
    const start = periodStart - i * periodLength;
    if (end <= floorStart) break;
    periods.push({
      start: Math.max(start, floorStart),
      end,
    });
  }

  return periods;
};

const HeaderHelp = ({ text }: { text: string }) => {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: -9999, left: -9999 });

  const tooltipCallbackRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node || !triggerRef.current) return;
      const r = triggerRef.current.getBoundingClientRect();
      const t = node.getBoundingClientRect();
      const gap = 8;
      const pad = 8;
      let top = r.bottom + gap;
      let left = r.left + r.width / 2 - t.width / 2;
      if (left < pad) left = pad;
      if (left + t.width > window.innerWidth - pad)
        left = window.innerWidth - pad - t.width;
      if (top + t.height > window.innerHeight - pad)
        top = r.top - t.height - gap;
      setCoords({ top, left });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visible]
  );

  return (
    <span
      ref={triggerRef}
      className="ml-1 inline-flex cursor-help align-middle"
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      <InformationCircleIcon className={INFO_ICON_CLASS} />
      {visible &&
        createPortal(
          <div
            ref={tooltipCallbackRef}
            role="tooltip"
            style={{
              position: "fixed",
              top: coords.top,
              left: coords.left,
              zIndex: 99999,
              pointerEvents: "none",
            }}
            className="w-72 max-w-[90vw] rounded-md bg-gray-900 px-3 py-2.5 text-left text-sm font-normal normal-case leading-relaxed text-white shadow-lg"
          >
            {text}
          </div>,
          document.body
        )}
    </span>
  );
};

const DetailsNutritionPlatform = () => {
  const flags = useFlags();
  const { setApiKey } = useApiKey();

  const [unauthorized, setUnauthorized] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [hideKey, setHideKey] = useState(true);
  const [productState, setProductState] = useState<ProductDataType>({
    isSubscribed: false,
    trialEnd: null,
    billingDate: new Date(),
    licenseKey: null,
    usageInfo: null,
    tiers: [],
  });

  useEffect(() => {
    if (productState?.licenseKey) {
      setApiKey(productState.licenseKey);
    }
  }, [productState?.licenseKey, setApiKey]);

  const [productSubDetail, setProductSubDetail] = useState<any>(null);
  const [autoRefillOnRenewal, setAutoRefillOnRenewal] = useState(0);
  const [refillBatchQuantity, setRefillBatchQuantity] = useState(1);
  const [refillBehaviour, setRefillBehaviour] = useState<"auto" | "off">(
    "auto"
  );
  const [refillSettingsSaving, setRefillSettingsSaving] = useState(false);
  const [refillNowLoading, setRefillNowLoading] = useState(false);
  const [predictedPercentage, setPredictedPercentage] = useState<number>(0);
  const [estimatorTierSlug, setEstimatorTierSlug] = useState<string>(
    TIERS[1].slug
  );
  const [estimatorUsers, setEstimatorUsers] = useState(100);
  const [estimatorCallsPerUserPerDay, setEstimatorCallsPerUserPerDay] =
    useState<Record<string, number>>({});

  const calculatePredictedUsage = (
    startDate: number,
    endDate: number,
    currentUsage: number
  ): number => {
    const now = new Date();
    const start = new Date(startDate * 1000);
    const end = new Date(endDate * 1000);
    const totalDays = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
    let daysPassed = (now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
    daysPassed = Math.max(daysPassed, 1);
    if (daysPassed <= 0) return currentUsage;
    return (currentUsage / daysPassed) * totalDays;
  };

  useEffect(() => {
    if (productSubDetail?.stripeSubscription && productSubDetail.usageInfo) {
      const predicted = calculatePredictedUsage(
        productSubDetail.stripeSubscription.current_period_start,
        productSubDetail.stripeSubscription.current_period_end,
        productSubDetail.usageInfo.units
      );
      const usageQuota = getUsageQuota(productSubDetail.usageInfo);
      setPredictedPercentage(
        usageQuota > 0 ? (predicted / usageQuota) * 100 : 0
      );
    }
  }, [productSubDetail]);

  const groupModes = [
    ["Request Type", "type"],
    ["Request Path", "path"],
    ["AI Mode", "aiMode"],
  ];
  const [usageDetailGroupMode, setUsageDetailGroupMode] = useState(
    groupModes[0]
  );
  const [usageView, setUsageView] = useState<"basic" | "graph" | "history">(
    "basic"
  );
  const [usageHistoryForPeriod, setUsageHistoryForPeriod] =
    useState<UsageHistoryReportForPeriod | null>();
  const [historyOutputMode, setHistoryOutputMode] = useState<"grouped" | "raw">(
    "grouped"
  );
  const [historyStartDate, setHistoryStartDate] = useState("");
  const [historyEndDate, setHistoryEndDate] = useState("");
  const [isChatbotOpen, setIsChatbotOpen] = useState(false);
  const [isSdkChatbotOpen, setIsSdkChatbotOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<DetailsTab>(() =>
    getRequestedDetailsTab()
  );
  const [showEasyAISignIn, setShowEasyAISignIn] = useState(true);
  const [isActivatingEasyAI, setIsActivatingEasyAI] = useState(false);
  const [easyAIActivationMessage, setEasyAIActivationMessage] = useState("");
  const [easyAIAutoSignInTriggered, setEasyAIAutoSignInTriggered] =
    useState(false);

  const userContext = useContext(UserContext);
  const navigate = useNavigate();
  const [usageDetail, setUsageDetail] = useState<any>(null);
  const easyAIOwnerEmail = userContext?.email ?? "";
  const isNotSubscribedError = (err: AxiosError) =>
    err?.response?.status === 404 &&
    String(err?.response?.data?.cause || "")
      .toLowerCase()
      .includes("not subscribed");

  const patchLocalSubscriptionMetadata = (
    patch: Partial<{
      refillBehaviour: "auto" | "off";
      refillBatchQuantity: number;
      autoRefillOnRenewal: number;
    }>
  ) => {
    setProductSubDetail((prev: any) => {
      if (!prev?.stripeSubscription) return prev;
      const currentMd = prev?.stripeSubscription?.metadata || {};
      const nextMd = {
        ...currentMd,
        ...(patch.refillBehaviour !== undefined
          ? { refillBehaviour: patch.refillBehaviour }
          : {}),
        ...(patch.refillBatchQuantity !== undefined
          ? { refillBatchQuantity: String(patch.refillBatchQuantity) }
          : {}),
        ...(patch.autoRefillOnRenewal !== undefined
          ? {
              autoRefillOnRenewal: String(
                Math.max(0, patch.autoRefillOnRenewal)
              ),
            }
          : {}),
      };
      return {
        ...prev,
        stripeSubscription: {
          ...prev.stripeSubscription,
          metadata: nextMd,
        },
      };
    });
    if (patch.autoRefillOnRenewal !== undefined) {
      setAutoRefillOnRenewal(Math.max(0, patch.autoRefillOnRenewal));
    }
  };

  // Platform tier-based gating via subscription metadata
  const md = productSubDetail?.stripeSubscription?.metadata;
  const metadataRefillBatchQuantity = Math.max(
    1,
    parseInt(String(md?.refillBatchQuantity ?? "1"), 10) || 1
  );
  const metadataRefillBehaviour: "auto" | "off" =
    String(md?.refillBehaviour || "auto").toLowerCase() === "off"
      ? "off"
      : "auto";
  const metadataAutoRefillOnRenewal = Math.max(
    0,
    parseInt(String(md?.autoRefillOnRenewal ?? "0"), 10) || 0
  );
  const refillSettingsDirty =
    refillBatchQuantity !== metadataRefillBatchQuantity ||
    refillBehaviour !== metadataRefillBehaviour ||
    autoRefillOnRenewal !== metadataAutoRefillOnRenewal;
  const metadataFlagEnabled = (value: unknown): boolean =>
    String(value || "").toLowerCase() === "true";
  const tierName = String(md?.tier || "").toLowerCase();
  const currentTier = TIERS.find((t) => {
    const tierSlug = t.slug.toLowerCase();
    const tierShortSlug = tierSlug.replace("nutrition-platform-", "");
    const tierDisplay = t.name.toLowerCase();
    return (
      tierName === tierDisplay ||
      tierName === tierShortSlug ||
      tierName === tierSlug
    );
  });
  const insightsUnlocked =
    metadataFlagEnabled(md?.insightsAccess) ||
    currentTier?.features.insights === true;
  const aiInsightsUnlocked =
    metadataFlagEnabled(md?.aiInsightsAccess) ||
    currentTier?.features.aiInsights === true;
  const modelOverridesUnlocked =
    currentTier?.features.modelSwitching === true ||
    metadataFlagEnabled(md?.modelSelectAccess) ||
    metadataFlagEnabled(md?.modelSwitchingAccess) ||
    metadataFlagEnabled(md?.modelSwitching);
  const modelOverridesLockedTooltip =
    "Upgrade to Growth to set per-route default AI models.";
  const aiInsightsLockedTooltip = "AI insights are available on Pro plans.";
  const refillPricePerM = currentTier?.refillPricePerM ?? 25;
  const currentTierDisplayName =
    currentTier?.name || formatTierDisplayName(tierName);
  const tierSubscriptionPrice = getSubscriptionMonthlyFeeUsd(
    productSubDetail?.stripeSubscription
  );
  const immediateRefillCost = refillBatchQuantity * refillPricePerM;
  const renewalRefillCost = autoRefillOnRenewal * refillPricePerM;
  const currentTierBaseTokensM = parseInt(currentTier?.tokens || "0", 10) || 0;
  const effectiveUsageQuota = Math.max(
    getUsageQuota(productSubDetail?.usageInfo),
    getUsageQuota(productState?.usageInfo)
  );
  const currentUsageTokens = Math.max(
    0,
    Number(productSubDetail?.usageInfo?.units || 0)
  );
  const projectedPeriodUsageTokens = Math.max(
    0,
    (predictedPercentage * effectiveUsageQuota) / 100
  );
  const metadataRefilledTokens = Math.max(
    0,
    (parseFloat(String(md?.refilledAmount ?? "0")) || 0) * 1000000
  );
  const metadataBaseIncludedTokens = Math.max(
    0,
    (parseFloat(String(md?.quantity ?? "0")) || 0) * 1000000
  );
  const baseIncludedTokens = Math.max(
    0,
    metadataBaseIncludedTokens || currentTierBaseTokensM * 1000000
  );
  const metadataAllowanceTokens = baseIncludedTokens + metadataRefilledTokens;
  const totalAllowanceTokens = Math.max(
    0,
    effectiveUsageQuota,
    metadataAllowanceTokens
  );
  const includedAllowanceTokens = Math.min(
    baseIncludedTokens,
    totalAllowanceTokens
  );
  const refillAllowanceTokens = Math.max(
    0,
    totalAllowanceTokens - includedAllowanceTokens
  );
  const projectedPeriodOverageTokens = Math.max(
    0,
    projectedPeriodUsageTokens - totalAllowanceTokens
  );
  const projectedPeriodOverageCost =
    refillPricePerM * Math.ceil(projectedPeriodOverageTokens / 1000000);
  const refillCostSoFar = (metadataRefilledTokens / 1000000) * refillPricePerM;
  const additionalProjectedRefillCost = projectedPeriodOverageCost;
  const totalProjectedRefillCost =
    refillCostSoFar + additionalProjectedRefillCost;
  const configuredRefillBatchCost =
    metadataRefillBatchQuantity * refillPricePerM;
  const includedUsedTokens = Math.max(
    0,
    Math.min(currentUsageTokens, includedAllowanceTokens)
  );
  const refillUsedTokens = Math.max(
    0,
    Math.min(
      currentUsageTokens - includedAllowanceTokens,
      refillAllowanceTokens
    )
  );
  const meterCapacityTokens = Math.max(
    1,
    totalAllowanceTokens,
    currentUsageTokens,
    projectedPeriodUsageTokens
  );
  const includedAllowanceWidthPct = Math.min(
    100,
    (includedAllowanceTokens / meterCapacityTokens) * 100
  );
  const refillAllowanceWidthPct = Math.min(
    100 - includedAllowanceWidthPct,
    (refillAllowanceTokens / meterCapacityTokens) * 100
  );
  const includedUsedWidthPct = Math.min(
    includedAllowanceWidthPct,
    (includedUsedTokens / meterCapacityTokens) * 100
  );
  const refillUsedWidthPct = Math.min(
    refillAllowanceWidthPct,
    (refillUsedTokens / meterCapacityTokens) * 100
  );
  const projectedUsageWidthPct = Math.min(
    100,
    Math.max(0, (projectedPeriodUsageTokens / meterCapacityTokens) * 100)
  );
  const totalTokensAtRenewalM = currentTierBaseTokensM + autoRefillOnRenewal;
  const insightsLockedTooltip = insightsUnlocked
    ? undefined
    : "Insights & Settings are available on Growth and Pro plans.";
  const affiliateWorkflowEnabled = Boolean(
    flags["nutrition-platform-affiliate-earnings"]
  );
  const affiliateEarningsUnlocked =
    affiliateWorkflowEnabled &&
    (metadataFlagEnabled(md?.affiliateEarningsAccess) ||
      currentTier?.features?.affiliateEarnings !== false);
  const affiliateEarningsLockedTooltip = affiliateWorkflowEnabled
    ? "Affiliate earnings access is not enabled for this plan."
    : "Affiliate earnings is disabled by feature flag.";

  useEffect(() => {
    if (!affiliateWorkflowEnabled && activeTab === "affiliate") {
      setActiveTab("account");
    }
  }, [activeTab, affiliateWorkflowEnabled]);

  const isDevEnv =
    process.env.NODE_ENV === "development" ||
    process.env.REACT_APP_ENV === "development" ||
    window.location.hostname === "localhost" ||
    window.location.hostname.includes("-dev");
  const configuredEasyAIBaseUrl = String(
    process.env.REACT_APP_EASYAI_BASE_URL || ""
  )
    .trim()
    .replace(/\/+$/, "");
  const easyAIBaseUrl =
    configuredEasyAIBaseUrl ||
    (isDevEnv
      ? "https://easyai-dev.cloud.passiolife.com"
      : "https://easyai.passiolife.com");
  const easyAILoginUrl = `${easyAIBaseUrl}/login${
    easyAIOwnerEmail ? `?email=${encodeURIComponent(easyAIOwnerEmail)}` : ""
  }`;

  useEffect(() => {
    // Wait until subscription detail is loaded before enforcing insight locks.
    // Otherwise initial render (before metadata arrives) can incorrectly
    // downgrade tab=insights back to account.
    if (!productSubDetail || !productSubDetail.isSubscribed) return;
    if (activeTab === "insights" && !insightsUnlocked) {
      setActiveTab("account");
    }
    if (
      activeTab === "affiliate" &&
      (!affiliateWorkflowEnabled || !affiliateEarningsUnlocked)
    ) {
      setActiveTab("account");
    }
  }, [
    activeTab,
    insightsUnlocked,
    affiliateWorkflowEnabled,
    affiliateEarningsUnlocked,
    productSubDetail,
  ]);

  useEffect(() => {
    const syncTabFromUrl = () => setActiveTab(getRequestedDetailsTab());
    window.addEventListener("popstate", syncTabFromUrl);
    window.addEventListener("hashchange", syncTabFromUrl);
    return () => {
      window.removeEventListener("popstate", syncTabFromUrl);
      window.removeEventListener("hashchange", syncTabFromUrl);
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const nextHash = `#${detailsTabToHash(activeTab)}`;
    if (params.get("tab") === activeTab && window.location.hash === nextHash)
      return;
    params.set("tab", activeTab);
    const nextSearch = params.toString();
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}?${nextSearch}${nextHash}`
    );
  }, [activeTab]);

  // Data fetching - uses the "unified" endpoints since Platform shares the unified license
  const getProduct = async () =>
    await axios.get("/accounts/products/nutrition-platform/detail");
  const getProductUsageDetail = async () =>
    await axios.get(
      `/accounts/products/unified/usage/detail?groupMode=${usageDetailGroupMode[1]}&package=platform`
    );
  const getEasyAISSOURL = async () =>
    await axios.get(
      "/accounts/products/unified/easyai/sso-url?package=platform"
    );
  const getUsageHistoryDetail = async () => {
    const startUnix = Math.floor(
      new Date(historyStartDate + "T00:00:00").getTime() / 1000
    );
    const endUnix = Math.floor(
      new Date(historyEndDate + "T23:59:59").getTime() / 1000
    );
    return await axios.get(
      `/accounts/products/unified/usage/history?periodStart=${startUnix}&periodEnd=${endUnix}&package=platform&outputMode=${historyOutputMode}`
    );
  };

  const [usedPercentage, setUsedPercentage] = useState(0);
  const [billingPeriods, setBillingPeriods] = useState<
    { start: number; end: number }[]
  >([]);
  const [selectedPeriodIndex, setSelectedPeriodIndex] = useState(0);
  const selectedPeriod =
    billingPeriods.length > 0 ? billingPeriods[selectedPeriodIndex] : null;

  const { isLoading: usageDetailLoading, isFetching: usageDetailFetching } =
    useQuery(
      ["platformUsageDetail", userContext?.customerid, usageDetailGroupMode[1]],
      getProductUsageDetail,
      {
        enabled: !!productSubDetail?.isSubscribed && !!userContext?.customerid,
        onError: (err: AxiosError) => {
          if (isNotSubscribedError(err)) return;
          error(
            err?.response?.data?.cause ||
              "Could not fetch the products usage detail"
          );
          if (err?.response?.status === 403) setUnauthorized(true);
        },
        onSuccess: (res: AxiosResponse) => setUsageDetail(res?.data),
      }
    );

  const { data: easyAISSOResponse } = useQuery(
    ["easyAISSOURL", userContext?.customerid, productSubDetail?.isSubscribed],
    getEasyAISSOURL,
    {
      enabled:
        activeTab === "easyai" &&
        !!productSubDetail?.isSubscribed &&
        !!userContext?.customerid,
      staleTime: 1000 * 60 * 5,
      cacheTime: 1000 * 60 * 30,
      retry: false,
      onError: () => {
        // fall back to standard login URL without surfacing a user-facing error
      },
    }
  );

  const easyAISSOData: EasyAISSOResponse | undefined = easyAISSOResponse?.data;
  const easyAISignInUrl =
    easyAISSOData?.ssoEnabled && easyAISSOData?.ssoUrl
      ? easyAISSOData.ssoUrl
      : easyAILoginUrl;
  const [easyAIIframeUrl, setEasyAIIframeUrl] = useState(easyAILoginUrl);

  useEffect(() => {
    setEasyAIIframeUrl((prev) =>
      !prev || prev === "about:blank" ? easyAILoginUrl : prev
    );
  }, [easyAILoginUrl]);

  const resolveFreshEasyAISignInURL = async (): Promise<string> => {
    let nextURL = easyAISignInUrl;
    try {
      const res = await getEasyAISSOURL();
      const freshData: EasyAISSOResponse | undefined = res?.data;
      if (freshData?.ssoEnabled && freshData?.ssoUrl) {
        nextURL = freshData.ssoUrl;
      } else if (freshData?.loginUrl) {
        nextURL = freshData.loginUrl;
      }
    } catch {
      // Fall back to current launch URL if fresh token retrieval fails.
    }
    return nextURL;
  };

  const launchEasyAIInIframe = async () => {
    const nextURL = await resolveFreshEasyAISignInURL();
    setEasyAIIframeUrl("about:blank");
    window.requestAnimationFrame(() => setEasyAIIframeUrl(nextURL));
  };

  const openEasyAIInNewTab = async () => {
    const nextURL = await resolveFreshEasyAISignInURL();
    window.open(nextURL, "_blank", "noopener,noreferrer");
  };

  useEffect(() => {
    if (activeTab !== "easyai") {
      setEasyAIAutoSignInTriggered(false);
      return;
    }
    if (
      easyAIAutoSignInTriggered ||
      !productSubDetail?.isSubscribed ||
      !userContext?.customerid
    ) {
      return;
    }
    setEasyAIAutoSignInTriggered(true);
    launchEasyAIInIframe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    easyAIAutoSignInTriggered,
    productSubDetail?.isSubscribed,
    userContext?.customerid,
  ]);

  // [Platform Product] Global route averages for tier selection pricing card (all customers, cached 6h)
  const {
    data: globalRouteAveragesResponse,
    isLoading: globalAvgLoading,
    isFetching: globalAvgFetching,
  } = useQuery(
    ["globalRouteAverages", GLOBAL_ROUTE_AVG_DAYS],
    () =>
      axios.get(
        `/accounts/products/nutrition-platform/usage/route-averages/global?days=${GLOBAL_ROUTE_AVG_DAYS}`
      ),
    {
      enabled: true,
      staleTime: 1000 * 60 * 60 * 6,
      cacheTime: 1000 * 60 * 60 * 12,
      refetchOnWindowFocus: false,
      retry: 1,
      onError: (err: AxiosError) => {
        // eslint-disable-next-line no-console
        console.warn(
          "Failed to fetch global route averages:",
          err?.response?.status,
          err?.response?.data
        );
      },
    }
  );
  const globalRouteAverages = normalizeRouteAverageRows(
    globalRouteAveragesResponse?.data
  );
  const estimatorCategories = useMemo(
    () =>
      (globalRouteAverages.length > 0
        ? globalRouteAverages.map((row) => row.category)
        : DEFAULT_ROUTE_CALCULATOR_CATEGORIES
      ).filter((value, index, all) => all.indexOf(value) === index),
    [globalRouteAverages]
  );
  useEffect(() => {
    setEstimatorCallsPerUserPerDay((prev) => {
      let changed = false;
      const next: Record<string, number> = {};
      estimatorCategories.forEach((category) => {
        if (Object.prototype.hasOwnProperty.call(prev, category)) {
          next[category] = Math.max(0, Math.floor(Number(prev[category]) || 0));
        } else {
          changed = true;
          next[category] = DEFAULT_ROUTE_CALLS_PER_USER_PER_DAY[category] ?? 0;
        }
      });
      if (Object.keys(prev).length !== estimatorCategories.length) {
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [estimatorCategories]);
  const estimatorTier =
    TIERS.find((tier) => tier.slug === estimatorTierSlug) || TIERS[1];
  const estimatorUsersSafe = Math.max(
    0,
    Math.floor(Number(estimatorUsers) || 0)
  );
  const estimatorAvgTokenMap = new Map(
    globalRouteAverages.map((row) => [
      row.category,
      Math.max(0, row.avgBilledTokens),
    ])
  );
  const estimatorRows = estimatorCategories.map((category) => {
    const callsPerUserPerDay = Math.max(
      0,
      Math.floor(Number(estimatorCallsPerUserPerDay[category]) || 0)
    );
    const avgTokensPerCall = estimatorAvgTokenMap.get(category) || 0;
    const totalCallsPerDay = estimatorUsersSafe * callsPerUserPerDay;
    const tokensPerDay = totalCallsPerDay * avgTokensPerCall;
    return {
      category,
      callsPerUserPerDay,
      avgTokensPerCall,
      totalCallsPerDay,
      tokensPerDay,
    };
  });
  const estimatorTotals = estimatorRows.reduce(
    (acc, row) => ({
      callsPerDay: acc.callsPerDay + row.totalCallsPerDay,
      tokensPerDay: acc.tokensPerDay + row.tokensPerDay,
    }),
    { callsPerDay: 0, tokensPerDay: 0 }
  );
  const estimatorProjectedMonthlyTokens = estimatorTotals.tokensPerDay * 30;
  const estimatorIncludedTokens = estimatorTier.tokensMillions * 1000000;
  const estimatorProjectedOverageTokens = Math.max(
    0,
    estimatorProjectedMonthlyTokens - estimatorIncludedTokens
  );
  const estimatorProjectedOverageCost =
    (estimatorProjectedOverageTokens / 1000000) * estimatorTier.refillPricePerM;
  const estimatorReferenceValue =
    (estimatorProjectedMonthlyTokens / 1000000) * estimatorTier.refillPricePerM;
  const estimatorIncludedUsagePct =
    estimatorIncludedTokens > 0
      ? Math.min(
          100,
          (estimatorProjectedMonthlyTokens / estimatorIncludedTokens) * 100
        )
      : 0;

  const hasUsageThisBillingPeriod =
    Number(
      productSubDetail?.usageInfo?.units ?? productState?.usageInfo?.units ?? 0
    ) > 0;

  const {
    data: customerRouteAveragesResponse,
    isLoading: customerAvgLoading,
    isFetching: customerAvgFetching,
  } = useQuery(
    ["customerRouteAverages", userContext?.customerid, GLOBAL_ROUTE_AVG_DAYS],
    () =>
      axios.get(
        `/accounts/products/nutrition-platform/usage/route-averages?days=${GLOBAL_ROUTE_AVG_DAYS}`
      ),
    {
      enabled:
        !!productSubDetail?.isSubscribed &&
        !!userContext?.customerid &&
        hasUsageThisBillingPeriod,
      staleTime: 1000 * 60 * 10,
      cacheTime: 1000 * 60 * 30,
      refetchOnWindowFocus: false,
      retry: 1,
      onError: (err: AxiosError) => {
        if (isNotSubscribedError(err)) return;
        // eslint-disable-next-line no-console
        console.warn(
          "Failed to fetch customer route averages:",
          err?.response?.status,
          err?.response?.data
        );
      },
    }
  );

  const customerRouteAverages = normalizeRouteAverageRows(
    customerRouteAveragesResponse?.data
  );
  const routeAverages = customerRouteAverages;
  const routeAvgLoading = customerAvgLoading;
  const routeAvgFetching = customerAvgFetching;
  const routeTotals = useMemo(() => {
    const totalCalls = routeAverages.reduce(
      (sum, row) => sum + row.totalCalls,
      0
    );
    const totalBilledTokens = routeAverages.reduce(
      (sum, row) => sum + row.totalBilledTokens,
      0
    );
    const avgBilledTokensPerCall =
      totalCalls > 0 ? totalBilledTokens / totalCalls : 0;
    return {
      totalCalls,
      totalBilledTokens,
      avgBilledTokensPerCall,
    };
  }, [routeAverages]);

  const { isLoading: hisoryDataLoading, isFetching: historyDataFetching } =
    useQuery(
      [
        "platformUsageHistory",
        userContext?.customerid,
        historyStartDate,
        historyEndDate,
        historyOutputMode,
      ],
      getUsageHistoryDetail,
      {
        enabled:
          usageView === "history" && !!historyStartDate && !!historyEndDate,
        onError: (err: AxiosError) => {
          if (isNotSubscribedError(err)) return;
          error(err?.response?.data?.cause || "Could not fetch usage history");
          if (err?.response?.status === 403) setUnauthorized(true);
        },
        onSuccess: (res: AxiosResponse) => setUsageHistoryForPeriod(res?.data),
      }
    );

  const { isLoading, isFetching, refetch } = useQuery(
    ["platformProduct", userContext?.customerid],
    getProduct,
    {
      enabled: !!userContext?.customerid,
      onError: (err: AxiosError) => {
        error(err?.response?.data?.cause || "Could not fetch the products");
        if (err?.response?.status === 403) setUnauthorized(true);
      },
      onSuccess: (res: AxiosResponse) => {
        setProductSubDetail(res?.data);
        const usageQuota = getUsageQuota(res?.data?.usageInfo);
        setUsedPercentage(
          usageQuota > 0
            ? ((res?.data?.usageInfo?.units || 0) / usageQuota) * 100
            : 0
        );
        setProductState((prev) => ({
          ...prev,
          isSubscribed: res.data?.isSubscribed,
          trialEnd: res.data?.trialEnd ? new Date(res.data?.trialEnd) : null,
          billingDate: new Date(res.data?.billingDate),
          licenseKey: res.data?.licenseKey,
          usageInfo: res.data?.usageInfo,
          tiers: res.data?.tiers || [],
        }));
        // Populate refill settings from metadata
        const metadata = res.data?.stripeSubscription?.metadata || {};
        const renewalRefill = parseInt(
          metadata?.autoRefillOnRenewal ?? "0",
          10
        );
        const metadataBatch = parseInt(
          metadata?.refillBatchQuantity ?? "1",
          10
        );
        setAutoRefillOnRenewal(Math.max(0, renewalRefill || 0));
        setRefillBatchQuantity(Math.max(1, metadataBatch || 1));
        setRefillBehaviour(
          String(metadata?.refillBehaviour || "auto").toLowerCase() === "off"
            ? "off"
            : "auto"
        );
        const periods = buildBillingPeriods(res.data?.stripeSubscription);
        setBillingPeriods(periods);
        setSelectedPeriodIndex(0);
        if (periods.length > 0) {
          setHistoryStartDate(toInputDate(periods[0].start));
          setHistoryEndDate(toInputDate(periods[0].end));
        }
      },
    }
  );

  useEffect(() => {
    if (billingPeriods.length === 0) return;
    const safeIndex = Math.min(selectedPeriodIndex, billingPeriods.length - 1);
    if (safeIndex !== selectedPeriodIndex) {
      setSelectedPeriodIndex(safeIndex);
      return;
    }
    const period = billingPeriods[safeIndex];
    const nextStart = toInputDate(period.start);
    const nextEnd = toInputDate(period.end);
    if (historyStartDate !== nextStart) setHistoryStartDate(nextStart);
    if (historyEndDate !== nextEnd) setHistoryEndDate(nextEnd);
  }, [
    billingPeriods,
    selectedPeriodIndex,
    historyStartDate,
    historyEndDate,
    setHistoryStartDate,
    setHistoryEndDate,
  ]);

  async function copyTextToClipboard(text: string) {
    return await navigator.clipboard.writeText(text);
  }
  const handleCopyClick = () => {
    copyTextToClipboard(productState?.licenseKey ?? "").then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 1500);
    });
  };

  const handleActivateEasyAI = async () => {
    setIsActivatingEasyAI(true);
    setEasyAIActivationMessage("");
    try {
      const res = await axios.post(
        "/accounts/products/unified/easyai/activate"
      );
      if (res?.data?.result?.status === "ok") {
        setEasyAIActivationMessage(
          "Setup email sent. Open Easy AI and use Forgot password if needed."
        );
      } else {
        const fallback =
          res?.data?.result?.response_body || "Activation failed";
        setEasyAIActivationMessage(fallback);
        error(fallback);
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.cause ||
        err?.response?.data?.message ||
        err?.message ||
        "Activation failed";
      setEasyAIActivationMessage(msg);
      error(msg);
    } finally {
      setIsActivatingEasyAI(false);
    }
  };

  useEffect(() => {
    const getQueryParam = (param: string) =>
      new URLSearchParams(window.location.search).get(param);
    const encodedData = getQueryParam("event");
    if (encodedData) {
      const decodedString = atob(encodedData);
      try {
        ReactGA.event(JSON.parse(decodedString));
      } catch (e) {
        /* ignore */
      }
    }
  }, []);

  const handleDownloadCSV = () => {
    const blob = new Blob([usageHistoryForPeriod?.usageCSV || ""], {
      type: "text/csv",
    });
    const url = URL.createObjectURL(blob);
    const filename =
      historyOutputMode === "grouped"
        ? "usage_report_grouped.csv"
        : "usage_report_raw.csv";
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const saveRefillPreferences = async () => {
    setRefillSettingsSaving(true);
    try {
      const payload = {
        refillBehaviour,
        refillBatchQuantity,
        autoRefillOnRenewal,
      };
      await axios.put(
        "/accounts/products/nutrition-platform/subscription",
        payload
      );
      patchLocalSubscriptionMetadata(payload);
      success("Refill settings saved");
      refetch().catch(() => {});
      setTimeout(() => {
        refetch().catch(() => {});
      }, 1500);
    } catch (err: any) {
      error(err?.response?.data?.cause || "Failed to save refill settings");
    } finally {
      setRefillSettingsSaving(false);
    }
  };

  const handleRefillNow = async () => {
    setRefillNowLoading(true);
    try {
      await axios.post(
        "/accounts/products/nutrition-platform/subscription/refill"
      );
      success(
        "Refill processed successfully. Usage stats may take a few minutes to update."
      );
      await new Promise((resolve) => setTimeout(resolve, 2000));
      refetch().catch(() => {});
    } catch (err: any) {
      error(err?.response?.data?.cause || "Failed to refill");
    } finally {
      setRefillNowLoading(false);
    }
  };

  if (unauthorized) return <Navigate to="/access-restricted" />;

  // =========================================================================
  // NOT SUBSCRIBED - show tier pricing cards
  // Also show tier cards when user has no customerid (new user, query is disabled)
  // =========================================================================
  const queryDisabled = !userContext?.customerid;
  if (
    queryDisabled ||
    (!isFetching && !isLoading && !productState.isSubscribed)
  ) {
    return (
      <>
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="font-bold text-2xl xl:text-3xl text-gray-900">
              Nutrition AI Platform
            </h2>
            <p className="text-gray-500 mt-1">
              API access, mobile SDKs, Easy AI, and more
            </p>
          </div>
        </div>
        <p className="text-gray-600 mb-8 text-lg">
          Choose the plan that fits your needs. All plans include Easy AI, SDK
          access, localization, and the full REST API.
        </p>
        <div className="flex flex-wrap place-content-evenly items-stretch h-full gap-y-4 mb-8">
          {TIERS.map((tier) => (
            <SubscriptionCard
              key={tier.slug}
              name={tier.name}
              price={
                <>
                  <div className="text-gray-900 font-inter text-6xl font-extrabold leading-[60px]">
                    {tier.price}
                  </div>
                  <div className="text-gray-500 font-inter text-2xl font-medium leading-8">
                    /mo
                  </div>
                </>
              }
              counterInput={<></>}
              description="Choose this plan for your expected usage and feature needs."
              descriptionAlt={`${tier.refillPrice} refill`}
              bulletsHtml={[
                <span key={`${tier.slug}-included`}>
                  <span className="font-extrabold">{tier.tokens} </span>
                  <span>tokens per month included</span>
                </span>,
                ...(tier.features.easyAI
                  ? [
                      <span key={`${tier.slug}-easy`}>
                        Easy AI + API + SDK access
                      </span>,
                    ]
                  : []),
                ...(tier.features.localization
                  ? [
                      <span key={`${tier.slug}-localization`}>
                        Localization
                      </span>,
                    ]
                  : []),
                ...(tier.features.modelSwitching
                  ? [
                      <span key={`${tier.slug}-model-switching`}>
                        Model Switching
                      </span>,
                    ]
                  : []),
                ...(tier.features.insights
                  ? [<span key={`${tier.slug}-insights`}>Usage Insights</span>]
                  : []),
                ...(tier.features.aiInsights
                  ? [
                      <span key={`${tier.slug}-ai-insights`}>
                        AI-Powered Insights
                      </span>,
                    ]
                  : []),
                <span key={`${tier.slug}-support`}>
                  {tier.features.support}
                </span>,
              ]}
              bullets={[]}
              isCurrent={false}
              buttonText="Get Started"
              buttonOnClick={() =>
                navigate(`/manage-subscription?product=${tier.slug}`)
              }
              isCurrentButtonText="Get Started"
              isCurrentButtonOnClick={() =>
                navigate(`/manage-subscription?product=${tier.slug}`)
              }
              showCancelButton={false}
              cancelButtonOnClick={() => {}}
              disabled={false}
              wide={false}
              compact
            />
          ))}
        </div>

        <Card className="sm:p-6 mb-8 border-2 border-indigo-200 shadow-sm">
          <p className="font-semibold text-gray-900 mb-1">
            Monthly Usage Estimator (Calls to Tokens)
          </p>
          <p className="text-sm text-gray-500 mb-4">
            Estimate monthly token usage before subscribing. This helps you
            compare included tokens vs projected refill overage.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-gray-500 mb-1">
                Estimate Against Tier
              </label>
              <select
                value={estimatorTierSlug}
                onChange={(e) => setEstimatorTierSlug(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
              >
                {TIERS.map((tier) => (
                  <option key={tier.slug} value={tier.slug}>
                    {tier.name} ({tier.tokens} included, {tier.refillPrice}{" "}
                    refill)
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-gray-500 mb-1">
                Active Users
              </label>
              <input
                type="number"
                min={0}
                step={1}
                value={estimatorUsers}
                onChange={(e) =>
                  setEstimatorUsers(Math.max(0, Number(e.target.value) || 0))
                }
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-gray-500 mb-1">
                Refill Rate Used
              </label>
              <div className="h-[42px] rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 flex items-center">
                {estimatorTier.refillPrice}
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-100 text-left text-gray-600 text-xs uppercase tracking-wider">
                  <th className="py-2 pr-4">Route Category</th>
                  <th className="py-2 pr-4 text-right">Avg Tokens / Call</th>
                  <th className="py-2 pr-4 text-right">Calls/User/Day</th>
                  <th className="py-2 pr-4 text-right">Total Calls/Day</th>
                  <th className="py-2 text-right">Estimated Tokens/Day</th>
                </tr>
              </thead>
              <tbody>
                {estimatorRows.map((row) => (
                  <tr
                    key={row.category}
                    className="border-b border-gray-100 hover:bg-gray-50"
                  >
                    <td className="py-2.5 pr-4 text-gray-900">
                      {row.category}
                    </td>
                    <td className="py-2.5 pr-4 text-right text-gray-700">
                      {Math.round(row.avgTokensPerCall).toLocaleString()}
                    </td>
                    <td className="py-2.5 pr-4 text-right">
                      <input
                        type="number"
                        min={0}
                        step={1}
                        value={row.callsPerUserPerDay}
                        onChange={(e) =>
                          setEstimatorCallsPerUserPerDay((prev) => ({
                            ...prev,
                            [row.category]: Math.max(
                              0,
                              Math.floor(Number(e.target.value) || 0)
                            ),
                          }))
                        }
                        className="w-24 rounded-md border border-gray-300 px-2 py-1 text-sm text-right text-gray-900 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                      />
                    </td>
                    <td className="py-2.5 pr-4 text-right text-gray-700">
                      {Math.round(row.totalCallsPerDay).toLocaleString()}
                    </td>
                    <td className="py-2.5 text-right font-medium text-gray-900">
                      {formatTokenCount(row.tokensPerDay)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-300 font-semibold text-gray-900">
                  <td className="py-2.5 pr-4">Total / day</td>
                  <td className="py-2.5 pr-4 text-right">—</td>
                  <td className="py-2.5 pr-4 text-right">—</td>
                  <td className="py-2.5 pr-4 text-right">
                    {Math.round(estimatorTotals.callsPerDay).toLocaleString()}
                  </td>
                  <td className="py-2.5 text-right">
                    {formatTokenCount(estimatorTotals.tokensPerDay)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
              <p className="text-xs uppercase tracking-wider text-gray-500">
                Estimated Tokens / 30 Days
              </p>
              <p className="mt-1 text-lg font-semibold text-gray-900">
                {formatTokenCount(estimatorProjectedMonthlyTokens)}
              </p>
            </div>
            <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
              <p className="text-xs uppercase tracking-wider text-gray-500">
                Included Tokens / 30 Days
              </p>
              <p className="mt-1 text-lg font-semibold text-gray-900">
                {formatTokenCount(estimatorIncludedTokens)}
              </p>
              <p className="text-xs text-gray-500">
                {estimatorIncludedUsagePct.toFixed(1)}% of included used
              </p>
            </div>
            <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
              <p className="text-xs uppercase tracking-wider text-gray-500">
                Projected Additional Refill Cost
              </p>
              <p className="mt-1 text-lg font-semibold text-gray-900">
                {estimatorProjectedOverageCost.toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                })}
              </p>
              <p className="text-xs text-gray-500">
                {formatTokenCount(estimatorProjectedOverageTokens)} extra tokens
                at {estimatorTier.refillPrice}/1M
              </p>
            </div>
          </div>
          <EstimatedMonthlyCost
            tierName={estimatorTier.name}
            subscriptionPrice={estimatorTier.priceNum}
            includedTokensMillions={estimatorTier.tokensMillions}
            projectedOverageCost={estimatorProjectedOverageCost}
          />
          <p className="mt-3 text-xs text-gray-500">
            Reference value for all estimated usage (before included tokens):{" "}
            {estimatorReferenceValue.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            })}
            . Actual refill billing applies only to usage above included monthly
            tokens.
          </p>
        </Card>

        {/* Estimated cost per 1K calls table */}
        <Card className="sm:p-6 mb-8">
          <p className="font-semibold text-gray-900 mb-1">
            Route Cost Benchmarks
          </p>
          <p className="text-sm text-gray-500 mb-4">
            {globalRouteAverages.length > 0
              ? `Shows estimated refill value for 1,000 calls by route category using platform averages from the last ${GLOBAL_ROUTE_AVG_DAYS} days.`
              : "Loading platform-wide average token usage from recent production requests."}
          </p>
          <div className="mb-4 rounded-md border border-indigo-100 bg-indigo-50 px-3 py-2 text-sm text-indigo-900">
            <span className="font-semibold">Reference only:</span> this helps
            compare route efficiency. Refill billing starts only after included
            monthly tokens are used.
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100">
                <tr className="border-b border-gray-200 text-left text-gray-600 text-xs uppercase tracking-wider">
                  <th className="py-2 pr-4">Route Type</th>
                  <th className="py-2 pr-4 text-right">
                    Avg Tokens / Call (Platform 30d)
                    <HeaderHelp text="Average billed tokens for one request in this route category, using platform-wide data from the last 30 days." />
                  </th>
                  {TIERS.map((t) => (
                    <th key={t.name} className="py-2 text-right">
                      {t.name}
                      <br />
                      <span className="text-xs normal-case font-normal">
                        Cost for 1,000 calls ({t.refillPrice})
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {globalRouteAverages
                  .map((r) => ({
                    route: r.category,
                    avgTokens: Math.round(r.avgBilledTokens),
                  }))
                  .map((row) => (
                    <tr
                      key={row.route}
                      className="border-b border-gray-100 hover:bg-gray-50"
                    >
                      <td className="py-2.5 pr-4 text-gray-900">{row.route}</td>
                      <td className="py-2.5 pr-4 text-right font-medium text-indigo-600">
                        {globalAvgLoading || globalAvgFetching
                          ? "..."
                          : `~${row.avgTokens.toLocaleString()}`}
                      </td>
                      {TIERS.map((t) => (
                        <td
                          key={t.name}
                          className="py-2.5 text-right text-gray-700"
                        >
                          {(
                            (row.avgTokens * t.refillPricePerM * 1000) /
                            1000000
                          ).toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </td>
                      ))}
                    </tr>
                  ))}
                {!globalAvgLoading &&
                  !globalAvgFetching &&
                  globalRouteAverages.length === 0 && (
                    <tr>
                      <td
                        colSpan={2 + TIERS.length}
                        className="py-4 text-center text-sm text-gray-500"
                      >
                        No route-average data returned for the selected window.
                      </td>
                    </tr>
                  )}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-300">
                  <td
                    colSpan={2 + TIERS.length}
                    className="pt-3 text-xs text-gray-400"
                  >
                    Calls with billed tokens &gt;= 2,500,000 are excluded as
                    outliers.
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Card>
      </>
    );
  }

  // =========================================================================
  // SUBSCRIBED - full dashboard (mirrors Hub layout)
  // =========================================================================
  const currentPeriodStartUnix =
    billingPeriods[0]?.start ??
    Number(productSubDetail?.stripeSubscription?.current_period_start || 0);
  const currentPeriodEndUnix =
    billingPeriods[0]?.end ??
    Number(productSubDetail?.stripeSubscription?.current_period_end || 0);

  return (
    <>
      <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
        <h2 className="font-bold inter-bold text-md md:text-lg xl:text-3xl">
          Nutrition AI Platform Details
          {currentTier && (
            <span className={`ml-3 ${TIER_PILL_CLASS}`}>
              {currentTier.name}
            </span>
          )}
        </h2>
        <button
          type="button"
          onClick={() =>
            navigate(`/manage-subscription?product=${NUTRITION_PLATFORM_SLUG}`)
          }
          className="inline-flex items-center rounded-md border border-indigo-300 bg-white px-4 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-50"
        >
          Manage Subscription
        </button>
      </div>

      {/* ===== Tabs ===== */}
      <div className="mb-6">
        <div className="flex items-end gap-2 border-b border-gray-200">
          <button
            onClick={() => setActiveTab("account")}
            className={`px-6 py-3 text-lg font-semibold rounded-t-lg border border-b-0 ${
              activeTab === "account"
                ? "bg-white text-indigo-700 border-gray-200"
                : "bg-gray-100 text-gray-600 border-transparent hover:text-indigo-600"
            }`}
          >
            Account Info
          </button>
          <button
            onClick={() => setActiveTab("easyai")}
            className={`px-6 py-3 text-lg font-semibold rounded-t-lg border border-b-0 relative ${
              activeTab === "easyai"
                ? "bg-gradient-to-r from-indigo-50 to-purple-50 text-indigo-700 border-indigo-200"
                : "bg-gradient-to-r from-gray-100 to-indigo-50 text-gray-700 border-transparent hover:text-indigo-600 hover:border-indigo-100"
            }`}
          >
            <span className="flex items-center gap-2">
              <SparklesIcon className="h-5 w-5 text-yellow-500" />
              Easy AI
              <span className="absolute -top-2 -right-2 bg-gradient-to-r from-orange-500 to-pink-500 text-white text-xs font-bold px-2 py-0.5 rounded-full animate-pulse shadow-lg">
                NEW
              </span>
            </span>
          </button>
          <span title={insightsLockedTooltip}>
            <button
              onClick={() => {
                if (insightsUnlocked) setActiveTab("insights");
              }}
              className={`px-6 py-3 text-lg font-semibold rounded-t-lg border border-b-0 ${
                activeTab === "insights"
                  ? "bg-white text-indigo-700 border-gray-200"
                  : "bg-gray-100 text-gray-600 border-transparent hover:text-indigo-600"
              } ${
                insightsUnlocked
                  ? ""
                  : "cursor-not-allowed text-gray-400 hover:text-gray-400"
              }`}
              aria-disabled={!insightsUnlocked}
            >
              <span className="inline-flex items-center gap-2">
                Insights & Settings
                {!insightsUnlocked && (
                  <span className="inline-flex items-center rounded-full bg-gray-200 px-2 py-0.5 text-xs font-semibold text-gray-600">
                    Locked
                  </span>
                )}
              </span>
            </button>
          </span>
          {affiliateWorkflowEnabled && (
            <span title={affiliateEarningsLockedTooltip}>
              <button
                onClick={() => {
                  if (affiliateEarningsUnlocked) setActiveTab("affiliate");
                }}
                className={`px-6 py-3 text-lg font-semibold rounded-t-lg border border-b-0 ${
                  activeTab === "affiliate"
                    ? "bg-white text-indigo-700 border-gray-200"
                    : "bg-gray-100 text-gray-600 border-transparent hover:text-indigo-600"
                } ${
                  affiliateEarningsUnlocked
                    ? ""
                    : "cursor-not-allowed text-gray-400 hover:text-gray-400"
                }`}
                aria-disabled={!affiliateEarningsUnlocked}
              >
                <span className="inline-flex items-center gap-2">
                  Affiliate Earnings
                  {!affiliateEarningsUnlocked && (
                    <span className="inline-flex items-center rounded-full bg-gray-200 px-2 py-0.5 text-xs font-semibold text-gray-600">
                      Locked
                    </span>
                  )}
                </span>
              </button>
            </span>
          )}
        </div>
      </div>

      {/* ===== Account Info Tab ===== */}
      <div
        className={`lg:grid-cols-2 gap-8 auto-rows-auto ${
          activeTab === "account" ? "grid" : "hidden"
        }`}
      >
        {/* --- Usage Card --- */}
        <Card className="sm:p-6 lg:order-1">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex flex-col">
              <h3 className="text-lg font-semibold text-gray-900">
                Current Period
              </h3>
              <span className="text-sm text-gray-500">
                {currentPeriodStartUnix > 0 && currentPeriodEndUnix > 0 ? (
                  <>
                    {new Date(currentPeriodStartUnix * 1000).toLocaleDateString(
                      "default",
                      {
                        month: "2-digit",
                        year: "2-digit",
                        day: "2-digit",
                      }
                    )}
                    {"  -  "}
                    {new Date(currentPeriodEndUnix * 1000).toLocaleDateString(
                      "default",
                      {
                        month: "2-digit",
                        year: "2-digit",
                        day: "2-digit",
                      }
                    )}
                  </>
                ) : (
                  "Unavailable"
                )}
              </span>
            </div>
            <div className="flex flex-col items-start gap-2 sm:items-end">
              {!isFetching && !isLoading ? (
                productState.trialEnd ? (
                  <Badge isWarning className="px-3 py-1.5">
                    <ClockIcon
                      className="h-5 w-5 flex-shrink-0 mr-2"
                      aria-hidden="true"
                    />
                    Free Trial Ends on{" "}
                    {productState.trialEnd.toLocaleDateString()}
                  </Badge>
                ) : (
                  <Badge className="px-3 py-1.5">
                    <ClockIcon
                      className="h-5 w-5 flex-shrink-0 mr-2"
                      aria-hidden="true"
                    />
                    Billed Next on{" "}
                    {productState.billingDate.toLocaleDateString()}
                  </Badge>
                )
              ) : (
                <></>
              )}
              {currentTier && (
                <span className={TIER_PILL_CLASS}>
                  Tier: {currentTier.name}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-4 mt-4">
            <div className="flex flex-col justify-center items-center w-full">
              <div className="flex flex-col items-center w-full mb-2">
                <div className="flex space-x-2 mb-8">
                  {(["basic", "graph", "history"] as const).map((v) => (
                    <button
                      key={v}
                      onClick={() => setUsageView(v)}
                      className={`px-3 py-1 text-sm font-medium rounded-full ${
                        usageView === v
                          ? "bg-indigo-600 text-white"
                          : "bg-indigo-100 text-indigo-700 hover:bg-gray-300"
                      }`}
                    >
                      {v.charAt(0).toUpperCase() + v.slice(1)}
                    </button>
                  ))}
                </div>

                {isLoading || isFetching ? (
                  <Spinner className="!w-[2rem]" />
                ) : productSubDetail?.usageInfo?.units !== undefined ? (
                  <>
                    {/* ----- Basic View ----- */}
                    {usageView === "basic" && (
                      <>
                        <div className="text-passio-indigo text-md font-bold">
                          {convertToNumLetter(productSubDetail.usageInfo.units)}{" "}
                          /{" "}
                          {convertToNumLetter(
                            productSubDetail.usageInfo.unitsQuota
                          )}
                        </div>
                        <div className="flex justify-between w-full text-indigo-700 mb-1">
                          <span className="text-sm font-semibold">
                            {usedPercentage.toFixed(1)}% Used (
                            {predictedPercentage.toFixed(1)}% Projected for
                            period)
                          </span>
                          {usedPercentage < 100 && (
                            <span className="text-sm font-semibold">
                              {(100 - usedPercentage).toFixed(1)}% Available
                            </span>
                          )}
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-6 relative overflow-hidden border border-gray-200">
                          <div
                            className="absolute inset-y-0 left-0 bg-indigo-100"
                            style={{ width: `${includedAllowanceWidthPct}%` }}
                          />
                          <div
                            className="absolute inset-y-0 bg-emerald-100"
                            style={{
                              left: `${includedAllowanceWidthPct}%`,
                              width: `${refillAllowanceWidthPct}%`,
                            }}
                          />
                          <div
                            className="absolute inset-y-0 left-0 bg-indigo-600"
                            style={{ width: `${includedUsedWidthPct}%` }}
                          />
                          {refillUsedWidthPct > 0 && (
                            <div
                              className="absolute inset-y-0 bg-emerald-600"
                              style={{
                                left: `${includedAllowanceWidthPct}%`,
                                width: `${refillUsedWidthPct}%`,
                              }}
                            />
                          )}
                          <div
                            className="absolute inset-y-0 border-l-2 border-amber-500"
                            style={{
                              left: `calc(${Math.max(
                                1,
                                projectedUsageWidthPct
                              )}% - 1px)`,
                            }}
                          />
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-gray-700">
                          <span className="inline-flex items-center gap-1">
                            <span className="h-2.5 w-2.5 rounded-full bg-indigo-600" />
                            Used Included Tokens
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <span className="h-2.5 w-2.5 rounded-full bg-emerald-600" />
                            Used Refill Tokens
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <span className="h-2.5 w-2.5 rounded-full bg-indigo-100 border border-indigo-200" />
                            Included Tokens
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <span className="h-2.5 w-2.5 rounded-full bg-emerald-100 border border-emerald-200" />
                            Refilled Tokens
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <span className="h-3 w-0 border-l-2 border-amber-500" />
                            Projected Usage
                          </span>
                        </div>
                        {effectiveUsageQuota > 0 && (
                          <div className="w-full mt-2 rounded-md border border-indigo-100 bg-indigo-50 px-3 py-3 text-sm text-indigo-900">
                            <div className="space-y-1.5">
                              <p className="font-semibold text-xs uppercase tracking-wide text-indigo-600 mb-1">
                                Plan Pricing
                              </p>
                              <div className="flex items-center justify-between gap-3">
                                <span>Subscription tier</span>
                                <span className="font-semibold">
                                  {currentTierDisplayName}
                                </span>
                              </div>
                              <div className="flex items-center justify-between gap-3">
                                <span>Tier price (monthly)</span>
                                <span className="font-semibold">
                                  {tierSubscriptionPrice > 0
                                    ? formatUsd(tierSubscriptionPrice)
                                    : "Unavailable"}
                                </span>
                              </div>
                              <div className="flex items-center justify-between gap-3">
                                <span>Refill rate</span>
                                <span className="font-semibold">
                                  {formatUsd(refillPricePerM)} / 1M tokens
                                </span>
                              </div>
                              <div className="flex items-center justify-between gap-3">
                                <span>Refill batch size</span>
                                <span className="font-semibold">
                                  {metadataRefillBatchQuantity}M tokens
                                </span>
                              </div>
                              <div className="flex items-center justify-between gap-3 font-semibold text-green-700">
                                <span>Refill price per batch</span>
                                <span>
                                  {formatUsd(configuredRefillBatchCost)}
                                </span>
                              </div>
                              <div className="border-t border-indigo-200 my-1" />
                              <p className="font-semibold text-xs uppercase tracking-wide text-indigo-600 mb-1">
                                Token Budget
                              </p>
                              <div className="flex items-center justify-between gap-3">
                                <span>Included tokens (plan)</span>
                                <span className="font-semibold">
                                  {convertToNumLetter(includedAllowanceTokens)}
                                </span>
                              </div>
                              <div className="flex items-center justify-between gap-3">
                                <span>Refilled tokens (purchased)</span>
                                <span className="font-semibold">
                                  {convertToNumLetter(refillAllowanceTokens)}
                                </span>
                              </div>
                              <div className="flex items-center justify-between gap-3 font-semibold">
                                <span>Total available</span>
                                <span>
                                  {convertToNumLetter(totalAllowanceTokens)}
                                </span>
                              </div>
                              <div className="border-t border-indigo-200 my-1" />
                              <p className="font-semibold text-xs uppercase tracking-wide text-indigo-600 mb-1">
                                Usage
                              </p>
                              <div className="flex items-center justify-between gap-3">
                                <span>Used from included tokens</span>
                                <span className="font-semibold">
                                  {convertToNumLetter(
                                    Math.round(includedUsedTokens)
                                  )}
                                </span>
                              </div>
                              <div className="flex items-center justify-between gap-3">
                                <span>Used from refill tokens</span>
                                <span className="font-semibold">
                                  {convertToNumLetter(
                                    Math.round(refillUsedTokens)
                                  )}
                                </span>
                              </div>
                              <div className="flex items-center justify-between gap-3 font-semibold">
                                <span>Total used</span>
                                <span>
                                  {convertToNumLetter(
                                    Math.round(currentUsageTokens)
                                  )}{" "}
                                  / {convertToNumLetter(totalAllowanceTokens)}
                                </span>
                              </div>
                              <div className="flex items-center justify-between gap-3 text-amber-700">
                                <span>Projected usage this period</span>
                                <span className="font-semibold">
                                  {convertToNumLetter(
                                    Math.round(projectedPeriodUsageTokens)
                                  )}
                                </span>
                              </div>
                              <div className="border-t border-indigo-200 my-1" />
                              <p className="font-semibold text-xs uppercase tracking-wide text-green-700 mb-1">
                                Refill Costs
                              </p>
                              <div className="flex items-center justify-between gap-3">
                                <span>Refill cost so far</span>
                                <span className="font-semibold">
                                  {refillCostSoFar.toLocaleString("en-US", {
                                    style: "currency",
                                    currency: "USD",
                                  })}
                                </span>
                              </div>
                              <div className="flex items-center justify-between gap-3">
                                <span>Additional projected refill cost</span>
                                <span className="font-semibold">
                                  {additionalProjectedRefillCost.toLocaleString(
                                    "en-US",
                                    {
                                      style: "currency",
                                      currency: "USD",
                                    }
                                  )}
                                </span>
                              </div>
                              <div className="flex items-center justify-between gap-3 font-semibold">
                                <span>Total projected refill cost</span>
                                <span>
                                  {totalProjectedRefillCost.toLocaleString(
                                    "en-US",
                                    {
                                      style: "currency",
                                      currency: "USD",
                                    }
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {/* ----- History View ----- */}
                    {usageView === "history" && (
                      <>
                        {!historyDataFetching && !hisoryDataLoading ? (
                          <div className="space-y-4 w-full">
                            <div className="rounded-xl border border-gray-200 bg-gray-50/70 p-4">
                              <label
                                htmlFor="platform-billing-period"
                                className="block text-sm font-medium text-gray-700 mb-2"
                              >
                                Billing Period
                              </label>
                              {billingPeriods.length > 0 ? (
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() =>
                                      setSelectedPeriodIndex((prev) =>
                                        Math.min(
                                          prev + 1,
                                          billingPeriods.length - 1
                                        )
                                      )
                                    }
                                    disabled={
                                      selectedPeriodIndex >=
                                      billingPeriods.length - 1
                                    }
                                    className="p-2 rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                    title="Previous billing period"
                                  >
                                    <ChevronLeftIcon className="w-4 h-4" />
                                  </button>
                                  <select
                                    id="platform-billing-period"
                                    className="flex-1 pl-3 pr-10 py-2 text-sm border border-gray-300 bg-white rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                    value={selectedPeriodIndex}
                                    onChange={(e) =>
                                      setSelectedPeriodIndex(
                                        parseInt(e.target.value, 10)
                                      )
                                    }
                                  >
                                    {billingPeriods.map((period, index) => (
                                      <option key={index} value={index}>
                                        {new Date(
                                          period.start * 1000
                                        ).toLocaleDateString()}{" "}
                                        -{" "}
                                        {new Date(
                                          period.end * 1000
                                        ).toLocaleDateString()}
                                        {index === 0 ? " (current)" : ""}
                                      </option>
                                    ))}
                                  </select>
                                  <button
                                    onClick={() =>
                                      setSelectedPeriodIndex((prev) =>
                                        Math.max(prev - 1, 0)
                                      )
                                    }
                                    disabled={selectedPeriodIndex <= 0}
                                    className="p-2 rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                    title="Next billing period"
                                  >
                                    <ChevronRightIcon className="w-4 h-4" />
                                  </button>
                                </div>
                              ) : (
                                <p className="text-sm text-gray-500">
                                  Billing periods are not available yet for this
                                  subscription.
                                </p>
                              )}
                              {selectedPeriod && (
                                <p className="text-xs text-gray-500 mt-2">
                                  Loading usage for{" "}
                                  {new Date(
                                    selectedPeriod.start * 1000
                                  ).toLocaleDateString()}{" "}
                                  through{" "}
                                  {new Date(
                                    selectedPeriod.end * 1000
                                  ).toLocaleDateString()}
                                  .
                                </p>
                              )}
                            </div>

                            <div className="flex flex-wrap items-center gap-3 justify-between">
                              <div className="inline-flex rounded-md shadow-sm">
                                <button
                                  onClick={() =>
                                    setHistoryOutputMode("grouped")
                                  }
                                  className={`px-3 py-1.5 text-sm font-medium rounded-l-md border ${
                                    historyOutputMode === "grouped"
                                      ? "bg-indigo-600 text-white border-indigo-600"
                                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                                  }`}
                                >
                                  Grouped Output
                                </button>
                                <button
                                  onClick={() => setHistoryOutputMode("raw")}
                                  className={`px-3 py-1.5 text-sm font-medium rounded-r-md border-t border-b border-r ${
                                    historyOutputMode === "raw"
                                      ? "bg-indigo-600 text-white border-indigo-600"
                                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                                  }`}
                                >
                                  Raw Output
                                </button>
                              </div>
                              <Button
                                onClick={handleDownloadCSV}
                                className="whitespace-nowrap"
                                disabled={historyDataFetching}
                              >
                                <ArrowDownTrayIcon className="w-4 h-4 mr-1.5 inline" />
                                Download CSV
                              </Button>
                            </div>
                            {historyOutputMode === "raw" && (
                              <p className="text-xs text-amber-600">
                                Raw output includes every individual API call.
                                For high-usage periods this may be a large file.
                              </p>
                            )}

                            {/* Invoice Links */}
                            {usageHistoryForPeriod?.invoiceLinks &&
                              usageHistoryForPeriod.invoiceLinks.length > 0 && (
                                <div>
                                  <h3 className="text-sm font-medium text-gray-900 mb-2">
                                    Invoice Downloads
                                  </h3>
                                  <ul className="divide-y divide-gray-200 rounded-md border border-gray-200">
                                    {usageHistoryForPeriod.invoiceLinks.map(
                                      (link, index) => (
                                        <li
                                          key={`inv-${index}`}
                                          className="px-4 py-2 hover:bg-gray-50"
                                        >
                                          <a
                                            href={link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-indigo-600 hover:text-indigo-800 flex items-center text-sm"
                                          >
                                            <ArrowDownTrayIcon className="pr-2 w-5 h-5" />
                                            Download Period Invoice {index + 1}
                                          </a>
                                        </li>
                                      )
                                    )}
                                  </ul>
                                </div>
                              )}
                          </div>
                        ) : (
                          <Spinner className="!w-[2rem]" />
                        )}
                      </>
                    )}

                    {/* ----- Graph View ----- */}
                    {usageView === "graph" && (
                      <div className="w-full mt-4 items-center align-middle content-center">
                        <Plot
                          data={[
                            {
                              type: "bar" as const,
                              x: [
                                "Used",
                                "Remaining",
                                "Avg/Day",
                                "Projected Usage",
                                "Projected Overage",
                              ],
                              y: [
                                productSubDetail.usageInfo.units,
                                productSubDetail.usageInfo.unitsQuota -
                                  productSubDetail.usageInfo.units,
                                productSubDetail.usageInfo.units /
                                  ((new Date().getTime() -
                                    new Date(
                                      productSubDetail.stripeSubscription
                                        .current_period_start * 1000
                                    ).getTime()) /
                                    (1000 * 60 * 60 * 24)),
                                (predictedPercentage *
                                  productSubDetail.usageInfo.unitsQuota) /
                                  100,
                                Math.max(
                                  0,
                                  (predictedPercentage *
                                    productSubDetail.usageInfo.unitsQuota) /
                                    100 -
                                    productSubDetail.usageInfo.unitsQuota
                                ),
                              ],
                              text: [
                                convertToNumLetter(
                                  productSubDetail.usageInfo.units
                                ),
                                convertToNumLetter(
                                  productSubDetail.usageInfo.unitsQuota -
                                    productSubDetail.usageInfo.units
                                ),
                                convertToNumLetter(
                                  productSubDetail.usageInfo.units /
                                    ((new Date().getTime() -
                                      new Date(
                                        productSubDetail.stripeSubscription
                                          .current_period_start * 1000
                                      ).getTime()) /
                                      (1000 * 60 * 60 * 24))
                                ),
                                convertToNumLetter(
                                  (predictedPercentage *
                                    productSubDetail.usageInfo.unitsQuota) /
                                    100
                                ),
                                convertToNumLetter(
                                  Math.max(
                                    0,
                                    (predictedPercentage *
                                      productSubDetail.usageInfo.unitsQuota) /
                                      100 -
                                      productSubDetail.usageInfo.unitsQuota
                                  )
                                ),
                              ],
                              textposition: "auto" as const,
                              hoverinfo: "text" as const,
                              marker: {
                                color: [
                                  "#4F46E5",
                                  "#818CF8",
                                  "#A5B4FC",
                                  "#C7D2FE",
                                  "#a42820",
                                ],
                              },
                              yaxis: "y" as const,
                            },
                            {
                              type: "bar" as const,
                              x: ["Days Left"],
                              y: [
                                Math.ceil(
                                  (new Date(
                                    productSubDetail.stripeSubscription
                                      .current_period_end * 1000
                                  ).getTime() -
                                    new Date().getTime()) /
                                    (1000 * 60 * 60 * 24)
                                ),
                              ],
                              text: [
                                Math.ceil(
                                  (new Date(
                                    productSubDetail.stripeSubscription
                                      .current_period_end * 1000
                                  ).getTime() -
                                    new Date().getTime()) /
                                    (1000 * 60 * 60 * 24)
                                ).toString() + " days",
                              ],
                              textposition: "auto" as const,
                              hoverinfo: "text" as const,
                              marker: { color: "#10B981" },
                              yaxis: "y2" as const,
                            },
                          ]}
                          layout={{
                            title: "Token Usage Overview",
                            font: { size: 10 },
                            autosize: true,
                            height: 300,
                            margin: { l: 40, r: 40, t: 40, b: 80 },
                            bargap: 0.15,
                            yaxis: {
                              title: "Tokens",
                              side: "left" as const,
                              range: [
                                0,
                                Math.max(
                                  productSubDetail.usageInfo.unitsQuota,
                                  (predictedPercentage *
                                    productSubDetail.usageInfo.unitsQuota) /
                                    100
                                ),
                              ],
                            },
                            yaxis2: {
                              title: "Billing Period Days",
                              side: "right" as const,
                              overlaying: "y" as const,
                              rangemode: "tozero" as const,
                              range: [
                                0,
                                Math.max(
                                  31,
                                  Math.ceil(
                                    (new Date(
                                      productSubDetail.stripeSubscription
                                        .current_period_end * 1000
                                    ).getTime() -
                                      new Date().getTime()) /
                                      (1000 * 60 * 60 * 24)
                                  )
                                ),
                              ],
                              dtick: 5,
                            },
                            showlegend: false,
                          }}
                          useResizeHandler={true}
                          style={{ width: "auto", height: "220px" }}
                          config={{ displayModeBar: false, responsive: true }}
                        />
                      </div>
                    )}

                    {/* Projected Overage — only when projected > available */}
                    {projectedPeriodOverageTokens > 0 && (
                      <div className="w-full mt-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm">
                        <p className="font-semibold text-red-700 mb-2">
                          Projected to exceed available tokens
                        </p>
                        <div className="w-full bg-red-100 rounded-full h-4 relative overflow-hidden mb-3">
                          <div
                            className="absolute inset-y-0 left-0 bg-indigo-500 rounded-l-full"
                            style={{
                              width: `${Math.min(
                                100,
                                (currentUsageTokens /
                                  Math.max(1, projectedPeriodUsageTokens)) *
                                  100
                              )}%`,
                            }}
                          />
                          <div
                            className="absolute inset-y-0 border-l-2 border-emerald-600"
                            style={{
                              left: `${Math.min(
                                100,
                                (totalAllowanceTokens /
                                  Math.max(1, projectedPeriodUsageTokens)) *
                                  100
                              )}%`,
                            }}
                          />
                        </div>
                        <div className="flex flex-wrap gap-3 text-xs text-gray-600 mb-3">
                          <span className="inline-flex items-center gap-1">
                            <span className="h-2 w-2 rounded-full bg-indigo-500" />
                            Used ({convertToNumLetter(currentUsageTokens)})
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <span className="h-2.5 w-0 border-l-2 border-emerald-600" />
                            Available (
                            {convertToNumLetter(totalAllowanceTokens)})
                          </span>
                          <span className="inline-flex items-center gap-1 text-red-600 font-medium">
                            Projected:{" "}
                            {convertToNumLetter(projectedPeriodUsageTokens)}
                          </span>
                        </div>
                        <div className="space-y-1 text-red-800">
                          <div className="flex justify-between">
                            <span>Additional tokens needed</span>
                            <span className="font-semibold">
                              {convertToNumLetter(projectedPeriodOverageTokens)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>Additional refill cost</span>
                            <span className="font-semibold">
                              {additionalProjectedRefillCost.toLocaleString(
                                "en-US",
                                { style: "currency", currency: "USD" }
                              )}
                            </span>
                          </div>
                          <div className="flex justify-between font-bold border-t border-red-200 pt-1">
                            <span>Total projected refill cost</span>
                            <span>
                              {totalProjectedRefillCost.toLocaleString(
                                "en-US",
                                {
                                  style: "currency",
                                  currency: "USD",
                                }
                              )}
                            </span>
                          </div>
                        </div>
                        <p className="mt-2 text-xs text-red-600">
                          {productSubDetail.stripeSubscription.metadata
                            .refillBehaviour === "auto"
                            ? `Auto-refill is enabled at ${
                                currentTier?.refillPrice ??
                                "$" + refillPricePerM
                              } per 1M tokens.`
                            : "Auto-refill is off. Consider enabling it or purchasing tokens manually to avoid service interruption."}
                        </p>
                      </div>
                    )}
                    <span className="mt-2 text-sm text-gray-700">
                      *Usage and predictions in this dashboard may take a few
                      minutes to reflect the latest data. Your API client
                      responses will be up-to-date.
                    </span>
                  </>
                ) : (
                  "-"
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* --- API Key & Links Card --- */}
        <Card className="sm:p-6 lg:order-2">
          <div className="flex flex-col">
            <p className="font-medium">API Key & Info</p>
          </div>
          <div className="mt-6">
            <dt className="text-sm font-medium text-gray-500">API Key</dt>
            <dd className="mt-1 text-sm text-gray-900">
              <ul
                role="list"
                className="divide-y divide-gray-200 rounded-md border border-gray-200"
              >
                <li className="flex items-center justify-between py-3 pl-3 pr-4 text-sm">
                  <div className="flex w-0 flex-1 items-center">
                    <KeyIcon
                      className="h-5 w-5 flex-shrink-0 text-gray-400"
                      aria-hidden="true"
                    />
                    <span className="ml-2 w-0 flex-1 truncate">
                      {hideKey
                        ? Array.from({
                            length: productState?.licenseKey?.length || 0,
                          }).map((_, i) => <span key={i}>&#8226;</span>)
                        : productState?.licenseKey ?? ""}
                    </span>
                  </div>
                  <div className="ml-4 flex flex-row flex-shrink items-center space-x-2">
                    {isCopied ? (
                      <ClipboardDocumentCheckIcon className="h-5 w-5 flex-shrink-0 text-green-700" />
                    ) : (
                      <DocumentDuplicateIcon
                        className="h-5 w-5 flex-shrink-0 text-passio-indigo cursor-pointer"
                        onClick={handleCopyClick}
                      />
                    )}
                    {hideKey ? (
                      <EyeSlashIcon
                        className="h-5 w-5 flex-shrink-0 text-passio-indigo cursor-pointer"
                        onClick={() => setHideKey(false)}
                      />
                    ) : (
                      <EyeIcon
                        className="h-5 w-5 flex-shrink-0 text-passio-indigo cursor-pointer"
                        onClick={() => setHideKey(true)}
                      />
                    )}
                  </div>
                </li>
              </ul>
              <p className="text-gray-500 text-sm ml-4 mt-1">
                * Keep your API Key safe - see the best practices in the
                documentation.
              </p>
            </dd>
          </div>
          <div className="mt-6 rounded-xl border border-indigo-200 bg-gradient-to-r from-indigo-600 via-indigo-600 to-blue-600 p-5 shadow-lg">
            <div className="flex items-start gap-3">
              <div className="rounded-lg bg-white/15 p-2">
                <ChatBubbleLeftRightIcon className="h-6 w-6 text-white flex-shrink-0" />
              </div>
              <div className="flex-1">
                <p className="inline-flex items-center rounded-full bg-white/20 px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-white mb-2">
                  Fastest Path to Integration
                </p>
                <h3 className="text-base font-semibold text-white">
                  Get unstuck in minutes with the Integration Assistant
                </h3>
                <p className="mt-1.5 text-sm text-indigo-100">
                  Ask implementation questions, generate request examples, debug
                  auth headers, and speed up launch.
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => setIsChatbotOpen(true)}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-semibold rounded-md shadow-sm text-indigo-700 bg-white hover:bg-indigo-50 transition-colors"
                  >
                    <ChatBubbleLeftRightIcon className="h-5 w-5 mr-2" />
                    Open Integration Assistant
                  </button>
                  <button
                    onClick={() => setIsSdkChatbotOpen(true)}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-semibold rounded-md shadow-sm text-indigo-700 bg-white hover:bg-indigo-50 transition-colors"
                  >
                    <ChatBubbleLeftRightIcon className="h-5 w-5 mr-2" />
                    Open SDK Assistant
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-6">
            <dt className="text-sm font-semibold text-gray-700">
              Documentation & Support
            </dt>
            <dd className="mt-2 text-sm text-gray-900">
              <ul
                role="list"
                className="divide-y divide-gray-200 rounded-md border border-gray-200"
              >
                {SUPPORT_DOC_LINKS.map(({ href, label }) => (
                  <li
                    key={label}
                    className="flex items-center justify-between py-3 pl-3 pr-4 text-sm"
                  >
                    <div className="flex w-0 flex-1 items-center">
                      <ArrowTopRightOnSquareIcon className="h-5 w-5 flex-shrink-0 text-gray-400" />
                      <span className="ml-2 w-0 flex-1 truncate">
                        <a
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-indigo-600 hover:text-indigo-500"
                        >
                          {label}
                        </a>
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </dd>
          </div>
          <div className="mt-6">
            <dt className="text-sm font-medium text-gray-500">Other Links</dt>
            <dd className="mt-2 text-sm text-gray-900">
              <ul
                role="list"
                className="divide-y divide-gray-200 rounded-md border border-gray-200"
              >
                <li className="flex items-center justify-between py-3 pl-3 pr-4 text-sm">
                  <div className="flex w-0 flex-1 items-center">
                    <ArrowTopRightOnSquareIcon className="h-5 w-5 flex-shrink-0 text-gray-400" />
                    <span className="ml-2 w-0 flex-1 truncate">
                      <Link
                        to="/tools/mealplans"
                        className="font-medium text-indigo-600 hover:text-indigo-500"
                      >
                        Meal Planning Tool
                      </Link>
                    </span>
                  </div>
                </li>
              </ul>
            </dd>
          </div>
        </Card>

        {/* --- Easy AI Builder CTA --- */}
        <Card className="sm:p-6 bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 border-indigo-200 relative overflow-hidden lg:order-4">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-xl shadow-sm flex-shrink-0">
              <SparklesIcon className="h-7 w-7 text-indigo-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-lg font-semibold text-indigo-900">
                  Easy AI Builder
                </h3>
                <span className="bg-gradient-to-r from-orange-500 to-pink-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                  NEW
                </span>
              </div>
              <p className="text-sm text-indigo-800 mb-3">
                Build unlimited custom AI functions tailored to your exact needs
                — no coding or ML expertise required. Create production-ready
                API routes in seconds that work with any media type, any AI
                model, and chain multiple steps together.
              </p>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <div className="bg-white/60 rounded-md px-2.5 py-1.5 border border-indigo-100">
                  <p className="text-sm font-semibold text-indigo-800">
                    Any Media
                  </p>
                  <p className="text-xs text-gray-600">
                    Images, audio, video, PDFs
                  </p>
                </div>
                <div className="bg-white/60 rounded-md px-2.5 py-1.5 border border-indigo-100">
                  <p className="text-sm font-semibold text-indigo-800">
                    Any Model
                  </p>
                  <p className="text-xs text-gray-600">
                    GPT-4o, Claude, Gemini & more
                  </p>
                </div>
                <div className="bg-white/60 rounded-md px-2.5 py-1.5 border border-indigo-100">
                  <p className="text-sm font-semibold text-indigo-800">
                    Custom Chatbots
                  </p>
                  <p className="text-xs text-gray-600">
                    RAG with your knowledge bases
                  </p>
                </div>
                <div className="bg-white/60 rounded-md px-2.5 py-1.5 border border-indigo-100">
                  <p className="text-sm font-semibold text-indigo-800">
                    Chain Steps
                  </p>
                  <p className="text-xs text-gray-600">
                    Multi-step template pipelines
                  </p>
                </div>
              </div>
              <a
                href={easyAIBaseUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 transition-colors"
              >
                Open Easy AI Builder
                <ArrowTopRightOnSquareIcon className="h-4 w-4" />
              </a>
            </div>
          </div>
        </Card>

        {/* --- Unified Refill Controls Card --- */}
        {currentTier && productSubDetail ? (
          <Card className="sm:p-6 lg:order-3">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Token Refill Controls
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Configure instant refill behavior and optional renewal
                  pre-purchase in one place. Refill rate for your tier:{" "}
                  <span className="font-medium text-indigo-600">
                    {currentTier.refillPrice}
                  </span>
                  .
                </p>
              </div>
            </div>

            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <div>
                <label
                  htmlFor="refillBatchQuantity"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Refill quantity per purchase
                </label>
                <div className="flex items-end">
                  <input
                    type="number"
                    id="refillBatchQuantity"
                    value={refillBatchQuantity}
                    onChange={(e) =>
                      setRefillBatchQuantity(
                        Math.max(1, parseInt(e.target.value, 10) || 1)
                      )
                    }
                    className="w-24 px-3 py-2 text-sm rounded-md border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                  />
                  <span className="text-sm pl-2 whitespace-nowrap text-gray-600">
                    M tokens
                  </span>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Immediate refill charge:{" "}
                  <span className="font-semibold text-gray-700">
                    ${immediateRefillCost.toFixed(2)}
                  </span>
                </p>
                <button
                  onClick={handleRefillNow}
                  disabled={
                    refillSettingsDirty ||
                    refillNowLoading ||
                    refillSettingsSaving
                  }
                  className="mt-3 px-4 py-2 text-sm rounded-md border border-indigo-700 text-white bg-indigo-700 hover:bg-indigo-800 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {refillNowLoading ? "Purchasing..." : "Refill Now"}
                </button>
              </div>

              <div>
                <label
                  htmlFor="refillBehaviour"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Service behavior when balance is low
                </label>
                <select
                  id="refillBehaviour"
                  value={refillBehaviour}
                  onChange={(e) =>
                    setRefillBehaviour(e.target.value as "auto" | "off")
                  }
                  className="w-full px-3 py-2 text-sm rounded-md border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                >
                  <option value="auto">Auto-refill and continue service</option>
                  <option value="off">
                    Stop requests when tokens are exhausted
                  </option>
                </select>
                <p className="text-sm text-gray-500 mt-1">
                  Auto-refill charges one refill batch at request time.
                </p>
              </div>
            </div>

            <div className="mt-5 border-t border-gray-200 pt-4">
              <p className="block text-sm font-medium text-gray-700 mb-1">
                Auto pre-purchase on subscription renewal
              </p>
              <p className="text-sm text-gray-500 mt-1">
                Adds tokens at each renewal and charges at your tier refill
                rate.
              </p>
              <div className="mt-2 flex items-end">
                <input
                  type="number"
                  id="autoRefillOnRenewal"
                  value={autoRefillOnRenewal}
                  onChange={(e) =>
                    setAutoRefillOnRenewal(
                      Math.max(0, parseInt(e.target.value, 10) || 0)
                    )
                  }
                  className="w-24 px-3 py-2 text-sm rounded-md border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                />
                <span className="text-sm pl-2 whitespace-nowrap text-gray-600">
                  M tokens at each renewal
                </span>
              </div>
            </div>

            <div className="mt-5 p-3 bg-gray-50 rounded-lg text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-gray-600">Subscription</span>
                <span className="font-medium text-gray-900">
                  {currentTier.price}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Included tokens</span>
                <span className="font-medium text-gray-900">
                  {currentTier.tokens}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Auto purchased tokens</span>
                <span className="font-medium text-gray-900">
                  {autoRefillOnRenewal}M
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Total tokens available</span>
                <span className="font-medium text-gray-900">
                  {totalTokensAtRenewalM}M
                </span>
              </div>
              {autoRefillOnRenewal > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">
                    Renewal pre-purchase charge
                  </span>
                  <span className="font-medium text-indigo-600">
                    +${renewalRefillCost.toFixed(2)}
                  </span>
                </div>
              )}
              <hr className="my-1" />
              <div className="flex justify-between font-semibold">
                <span className="text-gray-900">Estimated total per month</span>
                <span className="text-gray-900">
                  $
                  {(currentTier.priceNum + renewalRefillCost).toLocaleString(
                    "en-US",
                    {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }
                  )}
                </span>
              </div>
            </div>

            {refillSettingsDirty && (
              <p className="mt-3 text-sm text-amber-600">
                You have unsaved changes. Save settings before using
                &nbsp;&quot;Refill Now&quot; to apply your latest refill
                quantity and renewal amount.
              </p>
            )}

            {refillSettingsDirty && (
              <div className="mt-4 flex flex-wrap gap-3 justify-end">
                <button
                  onClick={saveRefillPreferences}
                  disabled={refillSettingsSaving}
                  className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
                >
                  {refillSettingsSaving ? "Saving..." : "Save Refill Settings"}
                </button>
              </div>
            )}
          </Card>
        ) : (
          <Card>
            <Spinner />
          </Card>
        )}

        {/* --- Usage Detail Card --- */}
        <div className="w-full col-span-2 h-min lg:order-5">
          {currentUsageTokens <= 0 ? (
            <Card className="sm:p-6">
              <p className="text-sm text-gray-500">
                Usage breakdown appears after your first API calls in this
                billing period.
              </p>
            </Card>
          ) : !usageDetailFetching && !usageDetailLoading && usageDetail ? (
            <UsageDetailCard
              usageDetail={usageDetail}
              usageDetailGroupMode={usageDetailGroupMode}
              setUsageDetailGroupMode={setUsageDetailGroupMode}
              groupModes={groupModes}
            />
          ) : (
            <Card>
              <Spinner />
            </Card>
          )}
        </div>

        {/* --- Pricing Estimate Card --- */}
        {hasUsageThisBillingPeriod && (
          <Card className="sm:p-6 col-span-1 lg:col-span-2 lg:order-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <p className="text-lg font-semibold text-gray-900">
                  Your Route Spend Benchmarks
                </p>
                <span className="text-sm text-gray-500">
                  Uses your last {GLOBAL_ROUTE_AVG_DAYS} days of calls and shows
                  reference pricing at your tier refill rate.
                </span>
              </div>
              <div className="flex items-center gap-2">
                {currentTier && (
                  <span className="text-sm font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 px-3 py-1 rounded">
                    Refill rate: {currentTier.refillPrice}
                  </span>
                )}
              </div>
            </div>
            <div className="mb-4 rounded-md border border-indigo-100 bg-indigo-50 px-3 py-2 text-sm text-indigo-900">
              <span className="font-semibold">Reference only:</span> this table
              shows what usage would cost at refill rates. Actual overage
              billing only starts after your included monthly tokens are
              exhausted.
            </div>
            {routeAvgLoading || routeAvgFetching ? (
              <Spinner className="!w-[2rem]" />
            ) : routeAverages.length > 0 ? (
              <table className="w-full text-sm">
                <thead className="bg-gray-100">
                  <tr className="border-b border-gray-200 text-left text-gray-600 text-xs uppercase tracking-wider">
                    <th className="py-2 pr-2">Route</th>
                    <th className="py-2 px-2 text-right whitespace-nowrap">
                      Avg Tok/Call
                      <HeaderHelp text="Average billed tokens for one request in this category from the table source window." />
                    </th>
                    <th className="py-2 px-2 text-right whitespace-nowrap">
                      Calls ({GLOBAL_ROUTE_AVG_DAYS}d)
                      <HeaderHelp text="Total number of calls seen in this category for the selected 30-day source window." />
                    </th>
                    <th className="py-2 px-2 text-right whitespace-nowrap">
                      Tokens ({GLOBAL_ROUTE_AVG_DAYS}d)
                      <HeaderHelp text="Total billed tokens consumed in this category for the selected 30-day source window." />
                    </th>
                    {currentTier && (
                      <th className="py-2 px-2 text-right whitespace-nowrap">
                        Cost/1K Calls
                        <HeaderHelp text="What 1,000 calls in this category would cost at your current tier's refill rate if those tokens were billable." />
                      </th>
                    )}
                    {currentTier && (
                      <th className="py-2 pl-2 text-right whitespace-nowrap">
                        Value ({GLOBAL_ROUTE_AVG_DAYS}d)
                        <HeaderHelp text="Reference value for the full call volume shown in this table, using your current tier's refill rate." />
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {routeAverages.map((row) => (
                    <tr
                      key={row.category}
                      className="border-b border-gray-100 hover:bg-gray-50"
                    >
                      <td className="py-2.5 pr-2 font-medium text-gray-900">
                        {row.category}
                      </td>
                      <td className="py-2.5 px-2 text-right text-indigo-600 font-semibold">
                        {convertToNumLetter(Math.round(row.avgBilledTokens))}
                      </td>
                      <td className="py-2.5 px-2 text-right text-gray-700">
                        {row.totalCalls.toLocaleString()}
                      </td>
                      <td className="py-2.5 px-2 text-right text-gray-700">
                        {convertToNumLetter(Math.round(row.totalBilledTokens))}
                      </td>
                      {currentTier && (
                        <td className="py-2.5 px-2 text-right font-semibold text-gray-900">
                          {(
                            (row.avgBilledTokens *
                              1000 *
                              currentTier.refillPricePerM) /
                            1000000
                          ).toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                          })}
                        </td>
                      )}
                      {currentTier && (
                        <td className="py-2.5 pl-2 text-right font-semibold text-gray-900">
                          {(
                            (row.totalBilledTokens *
                              currentTier.refillPricePerM) /
                            1000000
                          ).toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                          })}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-gray-300 font-semibold text-gray-900">
                    <td className="py-2.5 pr-2">
                      Total ({GLOBAL_ROUTE_AVG_DAYS}d)
                    </td>
                    <td className="py-2.5 px-2 text-right">
                      {convertToNumLetter(
                        Math.round(routeTotals.avgBilledTokensPerCall)
                      )}
                    </td>
                    <td className="py-2.5 px-2 text-right">
                      {routeTotals.totalCalls.toLocaleString()}
                    </td>
                    <td className="py-2.5 px-2 text-right">
                      {convertToNumLetter(
                        Math.round(routeTotals.totalBilledTokens)
                      )}
                    </td>
                    {currentTier && (
                      <td className="py-2.5 px-2 text-right text-gray-500">
                        —
                      </td>
                    )}
                    {currentTier && (
                      <td className="py-2.5 pl-2 text-right">
                        {(
                          (routeTotals.totalBilledTokens *
                            currentTier.refillPricePerM) /
                          1000000
                        ).toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })}
                      </td>
                    )}
                  </tr>
                </tfoot>
              </table>
            ) : (
              <p className="text-gray-500 text-sm">
                No route usage data is available yet for your last{" "}
                {GLOBAL_ROUTE_AVG_DAYS} days.
              </p>
            )}
          </Card>
        )}
      </div>

      {/* ===== Affiliate Earnings Tab ===== */}
      {affiliateWorkflowEnabled && (
        <div className={activeTab === "affiliate" ? "block" : "hidden"}>
          {affiliateEarningsUnlocked ? (
            <AffiliateEarnings />
          ) : (
            <Card className="sm:p-6 text-center">
              <p className="text-gray-500 text-lg">
                Affiliate earnings are not enabled for this plan.
              </p>
              <p className="text-gray-400 text-sm mt-2">
                Contact support or upgrade your subscription to unlock this
                view.
              </p>
            </Card>
          )}
        </div>
      )}

      {/* ===== Easy AI Tab ===== */}
      <div className={activeTab === "easyai" ? "block" : "hidden"}>
        <Card className="sm:p-0 overflow-hidden">
          <div className="mx-4 my-4 rounded-lg border border-amber-200 bg-amber-50 overflow-hidden">
            <button
              onClick={() => setShowEasyAISignIn((prev) => !prev)}
              className="w-full p-3 flex items-center justify-between hover:bg-amber-100/50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <InformationCircleIcon className="h-5 w-5 text-amber-600" />
                <span className="font-semibold text-amber-900">
                  Easy AI Sign-in Info
                </span>
              </div>
              <span className="text-sm font-semibold text-amber-700">
                {showEasyAISignIn ? "Hide" : "Show"}
              </span>
            </button>
            {showEasyAISignIn && (
              <div className="px-4 pb-4">
                <p className="text-sm text-amber-800 mb-3">
                  Passio SSO runs automatically when you open this tab.
                </p>
                <p className="text-sm text-amber-800 mb-3">
                  Use the same email as your current account:{" "}
                  <span className="font-mono bg-amber-100 px-1 rounded">
                    {easyAIOwnerEmail || "your email"}
                  </span>
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={openEasyAIInNewTab}
                    className="inline-flex items-center rounded-md border border-indigo-300 bg-indigo-100/70 px-3 py-2 text-sm font-semibold text-indigo-800 shadow-sm transition hover:bg-indigo-100"
                  >
                    Open in new tab
                    <ArrowTopRightOnSquareIcon className="ml-1.5 h-4 w-4" />
                  </button>
                  <button
                    onClick={handleActivateEasyAI}
                    disabled={isActivatingEasyAI}
                    className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-md hover:bg-indigo-700 disabled:opacity-60"
                  >
                    {isActivatingEasyAI ? "Sending..." : "Resend Setup Email"}
                  </button>
                </div>
                {easyAIActivationMessage && (
                  <p className="mt-2 text-sm text-indigo-700">
                    {easyAIActivationMessage}
                  </p>
                )}
              </div>
            )}
          </div>
          <div className="h-[95vh] min-h-[1080px]">
            <iframe
              src={easyAIIframeUrl}
              className="w-full h-full border-0"
              title="EasyAI"
            />
          </div>
        </Card>
      </div>

      {/* ===== Insights Tab (gated by insightsAccess metadata) ===== */}
      <div className={activeTab === "insights" ? "block" : "hidden"}>
        {insightsUnlocked ? (
          <RouteInsights
            onOpenEasyAI={() => setActiveTab("easyai")}
            onOpenUpgrade={() =>
              navigate(
                `/manage-subscription?product=${NUTRITION_PLATFORM_SLUG}`
              )
            }
            packageMode="platform"
            includedTokensPerPeriod={totalAllowanceTokens}
            baseIncludedTokensPerPeriod={baseIncludedTokens}
            refillBatchQuantity={metadataRefillBatchQuantity}
            tierName={tierName}
            subscriptionFeeUsd={tierSubscriptionPrice}
            pricePerMillionOverride={refillPricePerM}
            authoritativeUsageTokens={Number(
              productSubDetail?.usageInfo?.units ||
                productState?.usageInfo?.units ||
                0
            )}
            authoritativeProjectedPeriodTokens={projectedPeriodUsageTokens}
            modelOverridesUnlocked={modelOverridesUnlocked}
            modelOverridesLockedTooltip={modelOverridesLockedTooltip}
            aiInsightsUnlocked={aiInsightsUnlocked}
            aiInsightsLockedTooltip={aiInsightsLockedTooltip}
            subscriptionStartUnix={Number(
              productSubDetail?.stripeSubscription?.start_date || 0
            )}
            billingPeriodStartUnix={Number(
              productSubDetail?.stripeSubscription?.current_period_start || 0
            )}
            billingPeriodEndUnix={Number(
              productSubDetail?.stripeSubscription?.current_period_end || 0
            )}
          />
        ) : (
          <Card className="sm:p-6 text-center">
            <p className="text-gray-500 text-lg">
              Insights & Settings are available on Growth and Pro plans.
            </p>
            <p className="text-gray-400 text-sm mt-2">
              Upgrade your subscription to unlock route analytics and usage
              insights.
            </p>
          </Card>
        )}
      </div>

      {/* ===== Chatbot Overlay ===== */}
      {isChatbotOpen && (
        <div className="fixed inset-y-0 right-0 w-[550px] bg-white shadow-2xl z-50 flex flex-col border-l border-gray-200">
          <div className="flex items-center justify-between px-4 py-3 bg-indigo-600 text-white">
            <div className="flex items-center">
              <ChatBubbleLeftRightIcon className="h-5 w-5 mr-2" />
              <h2 className="text-lg font-semibold">Integration Assistant</h2>
            </div>
            <button
              onClick={() => setIsChatbotOpen(false)}
              className="p-1 hover:bg-indigo-700 rounded-md transition-colors"
              aria-label="Close chat"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <div className="flex-1 overflow-hidden">
            <iframe
              src="https://easyai.passiolife.com/embed/chatbot?chatbotId=39e9168b-0802-4fb1-b290-c33cdbd3c1ff&apiKey=ZT3ift4W1-d7IoOz_x3MQMng6yZocy-2UAXaz62AtEY&customerTag=easyaitest"
              className="w-full h-full border-0"
              title="Integration Assistant Chatbot"
            />
          </div>
          <div className="px-4 py-4 bg-gradient-to-r from-indigo-50 to-blue-50 border-t border-gray-200">
            <div className="text-center mb-3">
              <p className="text-sm text-gray-700 mb-2 font-medium">
                This chatbot was created in seconds using
              </p>
              <a
                href="https://easyai.cloud-prod.passiolife.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-indigo-600 to-blue-600 text-white text-sm font-semibold rounded-lg hover:from-indigo-700 hover:to-blue-700 transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
              >
                Passio EasyAI
                <ArrowTopRightOnSquareIcon className="ml-2 h-4 w-4" />
              </a>
            </div>
            <p className="text-sm text-gray-600 leading-relaxed">
              Create your own AI chatbots with custom knowledge from any file
              type, custom functions, and image understanding. All API routes
              are hosted and production-ready within 30 seconds.
            </p>
          </div>
        </div>
      )}
      {isChatbotOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-30 z-40 lg:hidden"
          onClick={() => setIsChatbotOpen(false)}
        />
      )}

      {/* ===== SDK Chatbot Overlay ===== */}
      {isSdkChatbotOpen && (
        <div className="fixed inset-y-0 right-0 w-[550px] bg-white shadow-2xl z-50 flex flex-col border-l border-gray-200">
          <div className="flex items-center justify-between px-4 py-3 bg-indigo-600 text-white">
            <div className="flex items-center">
              <ChatBubbleLeftRightIcon className="h-5 w-5 mr-2" />
              <h2 className="text-lg font-semibold">SDK Assistant</h2>
            </div>
            <button
              onClick={() => setIsSdkChatbotOpen(false)}
              className="p-1 hover:bg-indigo-700 rounded-md transition-colors"
              aria-label="Close chat"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <div className="flex-1 overflow-hidden">
            <iframe
              src="https://easyai.passiolife.com/embed/chatbot?chatbotId=8a3ca850-ffbc-4296-bd30-7efde9cf096b&apiKey=ZT3ift4W1-d7IoOz_x3MQMng6yZocy-2UAXaz62AtEY&customerTag=easyaitest"
              className="w-full h-full border-0"
              title="SDK Assistant Chatbot"
            />
          </div>
          <div className="px-4 py-4 bg-gradient-to-r from-indigo-50 to-blue-50 border-t border-gray-200">
            <div className="text-center mb-3">
              <p className="text-sm text-gray-700 mb-2 font-medium">
                This chatbot was created in seconds using
              </p>
              <a
                href="https://easyai.cloud-prod.passiolife.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-indigo-600 to-blue-600 text-white text-sm font-semibold rounded-lg hover:from-indigo-700 hover:to-blue-700 transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
              >
                Passio EasyAI
                <ArrowTopRightOnSquareIcon className="ml-2 h-4 w-4" />
              </a>
            </div>
            <p className="text-sm text-gray-600 leading-relaxed">
              Create your own AI chatbots with custom knowledge from any file
              type, custom functions, and image understanding. All API routes
              are hosted and production-ready within 30 seconds.
            </p>
          </div>
        </div>
      )}
      {isSdkChatbotOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-30 z-40 lg:hidden"
          onClick={() => setIsSdkChatbotOpen(false)}
        />
      )}
    </>
  );
};

export default DetailsNutritionPlatform;
